import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  ExpenseForm,
  ExpenseList,
  LinkRecurringDialog,
  MergeExpensesDialog,
  RecurringBillsDialog,
} from "../components/expenses";
import { DebtForm } from "../components/debts";
import { IncomeForm } from "../components/income";
import {
  Expense,
  Income,
  Debt,
  Group,
  ExpenseSection,
  ExpensePriority,
} from "../lib/types";
import { expenseUtils, incomeUtils } from "../lib";
import { TaxProfile } from "../lib/taxCalculatorUtils";
import { expenseAPI, incomeAPI } from "../services/api/api";
import debtDB from "../services/database/debtDB";
import {
  onTaxProfilesChanged,
  taxProfilesDB,
} from "../services/database/taxProfilesDB";
import { transactionService } from "../services/api/transactionService";
import { demoService } from "../services/demo/demoService";
import { plaidCreditCardService } from "../services/plaid/plaidCreditCardService";
import {
  plaidBankAccountService,
  BankAccountBalance,
} from "../services/plaid/plaidBankAccountService";
import { getImportColumnIndices } from "../services/expenses/importParser";
import { captureErrorWithContext, logger } from "../utils/sentry";
import { groupAPI, shareAPI } from "../services/api";
import FloatingActionMenu from "../components/common/FloatingActionMenu";
import { EmptyState, SearchFilterBar } from "../components/common";
import PageShareModal from "../components/common/PageShareModal";
import { useShareAction } from "../contexts/ShareActionContext";
import { useSearchParams } from "react-router-dom";
import { getCachedUser } from "../services/supabase/authStorage";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Fab,
  Drawer,
  IconButton,
  CircularProgress,
  Skeleton,
  Alert,
  Snackbar,
  Tooltip,
  useTheme,
  Button,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Switch,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import FunctionsIcon from "@mui/icons-material/Functions";
import { ShareRecordModal } from "../components/groups";
import LoadingTimerDebug from "../components/common/LoadingTimerDebug";
import { addDays, addMonths } from "date-fns";
import { getTodayLocal } from "../lib/dateUtils";
import {
  didAmountChange,
  didDueDateDayChange,
  findSeriesOccurrenceByDueDate,
  getFutureExpenseSeriesOccurrences,
  getExpenseSeriesRootId,
  isRecurringSeriesExpense,
  isSeriesOccurrence,
  getAvailableSeriesRoots,
} from "../lib/expenseSeriesUtils";
import { isPostponedNow, isDebtPostponed } from "../lib/recurringBillsUtils";
import {
  expensesProfilesDB,
  ExpensesProfile,
  ExpensesProfileSettings,
  onExpensesProfilesChanged,
} from "../services/database/expensesProfilesDB";

interface ExpensesPageProps {
  setPageTitle?: (title: string) => void;
}

type RecurringDeleteScope =
  | "just_this"
  | "this_and_future"
  | "all_past_present_future"
  | "this_and_past";

type RecurringEditScope = "just_this" | "this_and_future";

const BILL_BUDGET_SECTIONS: ExpenseSection[] = [
  "today",
  "tomorrow",
  "thisWeek",
  "next10Days",
  "thisMonth",
  "nextMonth",
];
const PLAID_DEBT_OVERRIDE_STORAGE_KEY = "plaidDebtOverrides";

type ExpensesSearchControlsProps = {
  appliedSearchQuery: string;
  onApplySearch: (query: string) => void;
  paidFilter: "all" | "paid" | "unpaid";
  setPaidFilter: (value: "all" | "paid" | "unpaid") => void;
  recurringFilter: "all" | "recurring" | "one-time";
  setRecurringFilter: (value: "all" | "recurring" | "one-time") => void;
  categoryFilter: string;
  setCategoryFilter: (value: string) => void;
  vendorFilter: string;
  setVendorFilter: (value: string) => void;
  expenses: Expense[];
  sharedOnly: boolean;
  toggleSharedOnly: () => void;
  allExpanded: boolean;
  toggleAllExpanded: () => void;
  groupingMode: "time" | "recurrence";
  onGroupingModeChange: (mode: "time" | "recurrence") => void;
};

const ExpensesSearchControls = React.memo<ExpensesSearchControlsProps>(
  function ExpensesSearchControls({
    appliedSearchQuery,
    onApplySearch,
    paidFilter,
    setPaidFilter,
    recurringFilter,
    setRecurringFilter,
    categoryFilter,
    setCategoryFilter,
    vendorFilter,
    setVendorFilter,
    expenses,
    sharedOnly,
    toggleSharedOnly,
    allExpanded,
    toggleAllExpanded,
    groupingMode,
    onGroupingModeChange,
  }) {
    const [draft, setDraft] = useState(appliedSearchQuery);
    useEffect(() => {
      setDraft(appliedSearchQuery);
    }, [appliedSearchQuery]);

    const filters = useMemo(
      () => [
        {
          key: "paid",
          label: "Paid",
          value: paidFilter,
          onChange: (value: string) =>
            setPaidFilter(value as typeof paidFilter),
          options: [
            { value: "all", label: "All" },
            { value: "paid", label: "Paid" },
            { value: "unpaid", label: "Unpaid" },
          ],
          minWidth: 120,
        },
        {
          key: "recurring",
          label: "Recurring",
          value: recurringFilter,
          onChange: (value: string) =>
            setRecurringFilter(value as typeof recurringFilter),
          options: [
            { value: "all", label: "All" },
            { value: "recurring", label: "Recurring" },
            { value: "one-time", label: "One-time" },
          ],
          minWidth: 140,
        },
        {
          key: "category",
          label: "Category",
          value: categoryFilter,
          onChange: (value: string) => setCategoryFilter(value),
          options: [
            { value: "all", label: "All" },
            ...Array.from(
              new Set(
                expenses
                  .map((expense) => expense.internalCategory)
                  .filter(Boolean)
              )
            ).map((category) => ({
              value: category,
              label: category,
            })),
          ],
          minWidth: 160,
        },
        {
          key: "vendor",
          label: "Vendor",
          value: vendorFilter,
          onChange: (value: string) => setVendorFilter(value),
          options: [
            { value: "all", label: "All" },
            ...Array.from(
              new Set(expenses.map((expense) => expense.vendor).filter(Boolean))
            ).map((vendor) => ({
              value: vendor,
              label: vendor,
            })),
          ],
          minWidth: 160,
        },
      ],
      [
        paidFilter,
        setPaidFilter,
        recurringFilter,
        setRecurringFilter,
        categoryFilter,
        setCategoryFilter,
        vendorFilter,
        setVendorFilter,
        expenses,
      ]
    );

    return (
      <SearchFilterBar
        searchQuery={draft}
        onSearchQueryChange={setDraft}
        onSearchApply={() => onApplySearch(draft)}
        searchPlaceholder="Search title or vendor"
        filters={filters}
        sharedToggle={{ value: sharedOnly, onToggle: toggleSharedOnly }}
        expandToggle={{ value: allExpanded, onToggle: toggleAllExpanded }}
        groupingToggle={{ value: groupingMode, onChange: onGroupingModeChange }}
      />
    );
  }
);

const SaveExpensesProfileDialog = React.memo(
  function SaveExpensesProfileDialog({
    open,
    onClose,
    onSave,
  }: {
    open: boolean;
    onClose: () => void;
    onSave: (name: string) => Promise<void>;
  }) {
    const [name, setName] = useState("");

    useEffect(() => {
      if (!open) return;
      setName("");
    }, [open]);

    const trimmed = name.trim();

    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Save Expenses Profile</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Profile Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              if (!trimmed) return;
              void onSave(trimmed);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!trimmed}
            onClick={() => void onSave(trimmed)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

const ExpensesPage: React.FC<ExpensesPageProps> = ({ setPageTitle }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [searchParams, setSearchParams] = useSearchParams();
  const { setShareHandler } = useShareAction();
  const EXPANDED_SECTION_STORAGE_KEY = "expenses_expanded_section";
  const EXPENSES_PROFILE_PARAM_KEY = "profile";
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [income, setIncome] = useState<Income[]>([]);
  const [debts, setDebts] = useState<Debt[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccountBalance[]>([]);
  const [linkableDebts, setLinkableDebts] = useState<Debt[]>([]);
  const [includePendingDepositsInOverdue, setIncludePendingDepositsInOverdue] =
    useState(true);
  const [
    includeAvailableBalancesInOverdue,
    setIncludeAvailableBalancesInOverdue,
  ] = useState(true);
  const [overdueIncomeBreakdownOpen, setOverdueIncomeBreakdownOpen] =
    useState(false);
  const [taxProfiles, setTaxProfiles] = useState<TaxProfile[]>([]);
  const [recurringEditDialogOpen, setRecurringEditDialogOpen] = useState(false);
  const [recurringEditTarget, setRecurringEditTarget] =
    useState<Expense | null>(null);

  const normalizeExpenseNumbers = (expense: Expense): void => {
    if (typeof expense.amount === "string") {
      expense.amount = parseFloat(expense.amount);
    }
    if (typeof expense.percentOfIncome === "string") {
      expense.percentOfIncome = parseFloat(expense.percentOfIncome);
    }
  };
  const recurringEditResolverRef = useRef<
    ((scope: RecurringEditScope | null) => void) | null
  >(null);
  const readPlaidDebtOverrides = useCallback((): Record<
    string,
    Partial<Debt>
  > => {
    try {
      const stored = localStorage.getItem(PLAID_DEBT_OVERRIDE_STORAGE_KEY);
      if (!stored) return {};
      const parsed = JSON.parse(stored);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      console.warn("[ExpensesPage] Failed to parse Plaid debt overrides", {
        error,
      });
      return {};
    }
  }, []);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadPerf, setLoadPerf] = useState<{
    cacheHydrated: boolean;
    cacheAgeMs: number | null;
    cacheParseMs: number | null;
    expensesFetchMs: number | null;
    incomeFetchMs: number | null;
    debtsFetchMs: number | null;
    networkTotalMs: number | null;
  }>({
    cacheHydrated: false,
    cacheAgeMs: null,
    cacheParseMs: null,
    expensesFetchMs: null,
    incomeFetchMs: null,
    debtsFetchMs: null,
    networkTotalMs: null,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentExpense, setCurrentExpense] = useState<Expense | undefined>(
    undefined
  );
  const [incomeDrawerOpen, setIncomeDrawerOpen] = useState(false);
  const [currentIncome, setCurrentIncome] = useState<Income | null>(null);
  const [isIncomeEditing, setIsIncomeEditing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [lastAnalysis, setLastAnalysis] = useState<string | null>(null);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [sharedOnly, setSharedOnly] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareExpense, setShareExpense] = useState<Expense | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [pageShareOpen, setPageShareOpen] = useState(false);
  const [linkRecurringDialogOpen, setLinkRecurringDialogOpen] = useState(false);
  // The recurring view is now a full page tab (not a modal), so it never
  // overlaps the drawers below; kept as a constant for their backdrop props.
  const recurringBillsOpen = false;
  const [debtDrawerOpen, setDebtDrawerOpen] = useState(false);
  const [currentDebt, setCurrentDebt] = useState<Debt | undefined>(undefined);
  const [linkRecurringTargetIds, setLinkRecurringTargetIds] = useState<
    string[]
  >([]);
  const [recurringDeleteDialogOpen, setRecurringDeleteDialogOpen] =
    useState(false);
  const [recurringDeleteTarget, setRecurringDeleteTarget] =
    useState<Expense | null>(null);
  const recurringDeleteResolverRef = useRef<
    ((scope: RecurringDeleteScope | null) => void) | null
  >(null);
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [pinnedExpenseIds, setPinnedExpenseIds] = useState<Set<string>>(() => {
    const saved = sessionStorage.getItem("expenses_pinned_ids");
    if (saved) {
      try {
        return new Set(JSON.parse(saved) as string[]);
      } catch {
        // Default to none pinned
      }
    }
    return new Set<string>();
  });
  const [paidFilter, setPaidFilter] = useState<"all" | "paid" | "unpaid">(
    "all"
  );
  const [recurringFilter, setRecurringFilter] = useState<
    "all" | "recurring" | "one-time"
  >("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [allExpanded, setAllExpanded] = useState(false);
  const [expandedSection, setExpandedSection] = useState<ExpenseSection | null>(
    () => {
      const saved = sessionStorage.getItem(EXPANDED_SECTION_STORAGE_KEY);
      if (saved) return saved as ExpenseSection;

      // Default: all sections collapsed except Within 30 Days.
      return "expected";
    }
  );
  // Chronological roll-up order for cumulative card totals (Overdue excluded
  // per requirements — it never participates in or contributes to a sum).
  //
  // Only the DISJOINT rolling day-windows belong here. The month cards
  // (thisMonth / "June", nextMonth / "July") are a CALENDAR axis: thisMonth
  // already contains every today/tomorrow/thisWeek/next10Days item, so rolling
  // it onto the day-window chain double-counts those items and inflates the
  // total (e.g. "June" showing a Within-30-Days style sum of $55k instead of
  // the ~$4.4k actually due in June). Keep months OUT of the chain so each
  // month card always shows its own calendar-month total.
  // Optional: fold Overdue into the cumulative roll-up. Off by default so
  // Overdue stays out of card sums; when on, it becomes the earliest bucket
  // in the chain and contributes to any card toggled cumulative.
  const CUMULATIVE_OVERDUE_KEY = "expenses_cumulative_include_overdue";
  const [includeOverdueInCumulative, setIncludeOverdueInCumulative] =
    useState<boolean>(() => {
      try {
        return sessionStorage.getItem(CUMULATIVE_OVERDUE_KEY) === "true";
      } catch {
        return false;
      }
    });
  const toggleIncludeOverdueInCumulative = useCallback(() => {
    setIncludeOverdueInCumulative((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem(CUMULATIVE_OVERDUE_KEY, String(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
  }, []);
  const CUMULATIVE_CHAIN = useMemo<ExpenseSection[]>(
    () =>
      includeOverdueInCumulative
        ? ["overdue", "today", "tomorrow", "thisWeek", "next10Days"]
        : ["today", "tomorrow", "thisWeek", "next10Days"],
    [includeOverdueInCumulative]
  );
  // The calendar axis rolls up separately: a month already contains every
  // day-window item, so months must never join the day-window chain. Rolling
  // month over month is still meaningful — "August cumulative" is August plus
  // July — and keeps each axis internally consistent.
  const CUMULATIVE_MONTH_CHAIN = useMemo<ExpenseSection[]>(
    () =>
      includeOverdueInCumulative
        ? ["overdue", "thisMonth", "nextMonth"]
        : ["thisMonth", "nextMonth"],
    [includeOverdueInCumulative]
  );
  const CUMULATIVE_STORAGE_KEY = "expenses_cumulative_sections";
  const [cumulativeSections, setCumulativeSections] = useState<
    Set<ExpenseSection>
  >(() => {
    try {
      const saved = sessionStorage.getItem(CUMULATIVE_STORAGE_KEY);
      if (saved) return new Set(JSON.parse(saved) as ExpenseSection[]);
    } catch {
      // ignore malformed storage
    }
    return new Set<ExpenseSection>();
  });

  const toggleCumulativeSection = useCallback((section: ExpenseSection) => {
    setCumulativeSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      try {
        sessionStorage.setItem(
          CUMULATIVE_STORAGE_KEY,
          JSON.stringify([...next])
        );
      } catch {
        // ignore storage failures
      }
      console.log("[ExpensesPage] Toggle cumulative", {
        section,
        cumulative: [...next],
      });
      return next;
    });
  }, []);

  // Sections rolled into a given card when cumulative: itself + all earlier
  // buckets on ITS OWN axis. Sections on neither axis return just themselves.
  const getCumulativeIncludedSections = useCallback(
    (section: ExpenseSection): ExpenseSection[] => {
      const monthIdx = CUMULATIVE_MONTH_CHAIN.indexOf(section);
      if (monthIdx >= 0) return CUMULATIVE_MONTH_CHAIN.slice(0, monthIdx + 1);
      const idx = CUMULATIVE_CHAIN.indexOf(section);
      if (idx < 0) return [section];
      return CUMULATIVE_CHAIN.slice(0, idx + 1);
    },
    [CUMULATIVE_CHAIN, CUMULATIVE_MONTH_CHAIN]
  );

  // Every card on an axis offers the toggle; only Overdue never does, since it
  // is the axis root rather than a bucket that can absorb earlier ones. For the
  // first bucket on an axis the roll-up adds Overdue when that is folded in,
  // and otherwise equals the card's own total.
  const canSectionCumulate = useCallback(
    (section: ExpenseSection): boolean =>
      section !== "overdue" &&
      (CUMULATIVE_CHAIN.includes(section) ||
        CUMULATIVE_MONTH_CHAIN.includes(section)),
    [CUMULATIVE_CHAIN, CUMULATIVE_MONTH_CHAIN]
  );

  const [newlyAddedExpenseId, setNewlyAddedExpenseId] = useState<string | null>(
    null
  );
  const handlePausedIdsChange = useCallback((ids: Set<string>) => {
    console.log("[ExpensesPage] Paused IDs updated:", [...ids]);
  }, []);

  const handleResetOccurrences = useCallback(
    async (expense: Expense): Promise<void> => {
      // Always regenerate from the series root id.
      const seriesId = expense.originalId || expense.id;
      console.log("[ExpensesPage] Reset occurrences", {
        clickedId: expense.id,
        seriesId,
        recurrencePattern: expense.recurrencePattern,
        isRecurring: expense.isRecurring,
        isRecurringInstance: expense.isRecurringInstance,
      });
      const { instancesCreated, instances } =
        await expenseAPI.regenerateInstances(seriesId, 24);

      if (instancesCreated > 0 && instances.length > 0) {
        // Normalize dates/numbers and merge.
        const normalized = instances.map((inst) => {
          const next = { ...inst } as Expense;
          if (typeof next.dueDate === "string") {
            next.dueDate = new Date(next.dueDate);
          }
          if (typeof next.invoicedDate === "string")
            next.invoicedDate = new Date(next.invoicedDate);
          normalizeExpenseNumbers(next);
          return next;
        });
        setExpenses((prev) => {
          const existingIds = new Set(prev.map((e) => e.id));
          const additions = normalized.filter((e) => !existingIds.has(e.id));
          return additions.length > 0 ? [...prev, ...additions] : prev;
        });
      }

      setUndoSnackbar({
        open: true,
        message:
          instancesCreated > 0
            ? `Reset occurrences: created ${instancesCreated} missing item${
                instancesCreated === 1 ? "" : "s"
              }`
            : "Reset occurrences: nothing missing",
      });
    },
    []
  );

  const handleLinkAsRecurring = useCallback(
    async (seriesRootId: string): Promise<void> => {
      const ids = linkRecurringTargetIds;
      const result = await expenseAPI.linkAsRecurring(ids, seriesRootId);
      const idSet = new Set(ids);
      setExpenses((prev) =>
        prev.map((e) =>
          idSet.has(e.id)
            ? {
                ...e,
                originalId: seriesRootId,
                isRecurringInstance: true,
                isRecurring: false,
              }
            : e
        )
      );
      setLinkRecurringDialogOpen(false);
      setLinkRecurringTargetIds([]);
      setUndoSnackbar({
        open: true,
        message: `Linked ${result.updated} expense${result.updated === 1 ? "" : "s"} as recurring`,
      });
    },
    [linkRecurringTargetIds]
  );

  const handleBulkLinkRecurring = useCallback((expenseIds: string[]) => {
    setLinkRecurringTargetIds(expenseIds);
    setLinkRecurringDialogOpen(true);
  }, []);

  const availableSeriesRoots = useMemo(
    () => (linkRecurringDialogOpen ? getAvailableSeriesRoots(expenses) : []),
    [linkRecurringDialogOpen, expenses]
  );

  // ----- Merge expenses -----
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTargetIds, setMergeTargetIds] = useState<string[]>([]);

  const handleOpenMergeDialog = useCallback((expenseIds: string[]) => {
    // Projected rows can't be merged (they aren't persisted yet).
    const ids = expenseIds.filter((id) => !id.startsWith("projected-"));
    if (ids.length < 2) {
      setUndoSnackbar({
        open: true,
        message: "Select at least 2 saved expenses to merge",
      });
      return;
    }
    setMergeTargetIds(ids);
    setMergeDialogOpen(true);
  }, []);

  const mergeCandidates = useMemo(
    () =>
      mergeDialogOpen
        ? expenses.filter((e) => mergeTargetIds.includes(e.id))
        : [],
    [mergeDialogOpen, mergeTargetIds, expenses]
  );

  const handleMergeExpenses = useCallback(
    async (opts: {
      survivorId: string;
      newDate: Date;
      makeRecurring: boolean;
      recurrencePattern: string | null;
    }): Promise<void> => {
      const { survivorId, newDate, makeRecurring, recurrencePattern } = opts;
      const sourceIds = mergeTargetIds.filter((id) => id !== survivorId);

      console.log("[ExpensesPage] Merge expenses", {
        survivorId,
        sourceIds,
        newDate: newDate.toISOString(),
        makeRecurring,
        recurrencePattern,
      });

      const result = await expenseAPI.merge({
        survivorId,
        sourceIds,
        newDate: newDate.toISOString(),
        makeRecurring,
        recurrencePattern,
        monthsAhead: 24,
      });

      const normalize = (inst: Expense): Expense => {
        const next = { ...inst } as Expense;
        if (typeof next.dueDate === "string")
          next.dueDate = new Date(next.dueDate);
        if (typeof next.invoicedDate === "string")
          next.invoicedDate = new Date(next.invoicedDate);
        normalizeExpenseNumbers(next);
        return next;
      };

      const survivor = normalize(result.survivor);
      const instances = (result.instances || []).map(normalize);

      setExpenses((prev) => {
        const removed = new Set<string>([
          ...result.deletedIds,
          ...sourceIds,
          survivor.id,
          ...instances.map((i) => i.id),
        ]);
        const next = prev.filter((e) => !removed.has(e.id));
        return [...next, survivor, ...instances];
      });

      setMergeDialogOpen(false);
      setMergeTargetIds([]);
      setUndoSnackbar({
        open: true,
        message: `Merged ${sourceIds.length + 1} expenses into "${survivor.name}"`,
      });
    },
    [mergeTargetIds]
  );

  // Link (or clear) the bank transaction associated with an expense.
  const handleLinkTransaction = useCallback(
    async (expense: Expense, transactionId: string | null): Promise<void> => {
      console.log("[ExpensesPage] Link transaction", {
        expenseId: expense.id,
        transactionId,
      });
      try {
        // Pass null explicitly when clearing so the backend nulls the column.
        const updated = await expenseAPI.update({
          ...expense,
          linkedTransactionId: transactionId,
        } as Expense);
        normalizeExpenseNumbers(updated);
        setExpenses((prev) =>
          prev.map((e) => (e.id === expense.id ? { ...e, ...updated } : e))
        );
        setUndoSnackbar({
          open: true,
          message: transactionId
            ? `Linked transaction to "${expense.name}"`
            : `Unlinked transaction from "${expense.name}"`,
        });
        return;
      } catch (error) {
        console.error("[ExpensesPage] Link transaction failed", error);
        setUndoSnackbar({
          open: true,
          message: "Failed to update linked transaction",
        });
      }
    },
    []
  );

  const viewMode: "table" = "table";

  // Grouping mode: "time" (default) or "recurrence"
  const [groupingMode, setGroupingMode] = useState<"time" | "recurrence">(
    () => {
      const saved = sessionStorage.getItem("expenses_grouping_mode");
      return (saved as "time" | "recurrence") || "time";
    }
  );

  // Handle grouping mode change
  const handleGroupingModeChange = useCallback(
    (mode: "time" | "recurrence") => {
      setGroupingMode(mode);
      sessionStorage.setItem("expenses_grouping_mode", mode);
      console.log("[ExpensesPage] Grouping mode changed to:", mode);
    },
    []
  );

  const generateExpensesProfileId = (): string =>
    `expenses_profile_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  const [expenseProfiles, setExpenseProfiles] = useState<ExpensesProfile[]>([]);
  const [expenseProfileId, setExpenseProfileId] = useState<string>("");
  const [saveProfileDialogOpen, setSaveProfileDialogOpen] = useState(false);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const overdueTogglePersistedProfileRef = useRef<string | null>(null);

  const updateUrlWithProfile = useCallback(
    (profileId: string) => {
      const next = new URLSearchParams(window.location.search);
      if (profileId) {
        next.set(EXPENSES_PROFILE_PARAM_KEY, profileId);
      } else {
        next.delete(EXPENSES_PROFILE_PARAM_KEY);
      }
      setSearchParams(next, { replace: true });
    },
    [setSearchParams, EXPENSES_PROFILE_PARAM_KEY]
  );

  const buildCurrentProfileSettings =
    useCallback((): ExpensesProfileSettings => {
      return {
        version: 1,
        appliedSearchQuery,
        paidFilter,
        recurringFilter,
        categoryFilter,
        vendorFilter,
        sharedOnly,
        groupingMode,
        expandedSection,
        allExpanded,
        pinnedExpenseIds: [...pinnedExpenseIds],
        includePendingDepositsInOverdue,
        includeAvailableBalancesInOverdue,
      };
    }, [
      allExpanded,
      appliedSearchQuery,
      categoryFilter,
      expandedSection,
      includeAvailableBalancesInOverdue,
      includePendingDepositsInOverdue,
      groupingMode,
      paidFilter,
      pinnedExpenseIds,
      recurringFilter,
      sharedOnly,
      vendorFilter,
    ]);

  const applyProfileSettings = useCallback(
    (raw: any) => {
      const settings = raw as Partial<ExpensesProfileSettings>;
      if (typeof settings.appliedSearchQuery === "string") {
        setAppliedSearchQuery(settings.appliedSearchQuery);
      }
      if (
        settings.paidFilter === "all" ||
        settings.paidFilter === "paid" ||
        settings.paidFilter === "unpaid"
      ) {
        setPaidFilter(settings.paidFilter);
      }
      if (
        settings.recurringFilter === "all" ||
        settings.recurringFilter === "recurring" ||
        settings.recurringFilter === "one-time"
      ) {
        setRecurringFilter(settings.recurringFilter);
      }
      if (typeof settings.categoryFilter === "string") {
        setCategoryFilter(settings.categoryFilter);
      }
      if (typeof settings.vendorFilter === "string") {
        setVendorFilter(settings.vendorFilter);
      }
      if (typeof settings.sharedOnly === "boolean") {
        setSharedOnly(settings.sharedOnly);
      }
      if (
        settings.groupingMode === "time" ||
        settings.groupingMode === "recurrence"
      ) {
        handleGroupingModeChange(settings.groupingMode);
      }
      if (typeof settings.allExpanded === "boolean") {
        setAllExpanded(settings.allExpanded);
      }
      if (Array.isArray(settings.pinnedExpenseIds)) {
        setPinnedExpenseIds(
          new Set(
            settings.pinnedExpenseIds.filter((v) => typeof v === "string")
          )
        );
      }
      if (typeof settings.includePendingDepositsInOverdue === "boolean") {
        setIncludePendingDepositsInOverdue(
          settings.includePendingDepositsInOverdue
        );
      }
      if (typeof settings.includeAvailableBalancesInOverdue === "boolean") {
        setIncludeAvailableBalancesInOverdue(
          settings.includeAvailableBalancesInOverdue
        );
      }
      if (
        settings.expandedSection === null ||
        typeof settings.expandedSection === "string"
      ) {
        const nextSection =
          (settings.expandedSection as ExpenseSection | null) ?? null;
        setExpandedSection(nextSection);
        if (nextSection) {
          sessionStorage.setItem(EXPANDED_SECTION_STORAGE_KEY, nextSection);
        } else {
          sessionStorage.removeItem(EXPANDED_SECTION_STORAGE_KEY);
        }
      }
    },
    [EXPANDED_SECTION_STORAGE_KEY, handleGroupingModeChange]
  );

  const selectedExpenseProfile = useMemo(() => {
    if (!expenseProfileId) return null;
    return expenseProfiles.find((p) => p.id === expenseProfileId) || null;
  }, [expenseProfileId, expenseProfiles]);

  useEffect(() => {
    if (!selectedExpenseProfile) return;
    applyProfileSettings(selectedExpenseProfile.settings);
  }, [applyProfileSettings, selectedExpenseProfile]);

  useEffect(() => {
    if (!selectedExpenseProfile) return;
    if (
      overdueTogglePersistedProfileRef.current !== selectedExpenseProfile.id
    ) {
      overdueTogglePersistedProfileRef.current = selectedExpenseProfile.id;
      return;
    }
    const nextSettings: ExpensesProfileSettings = {
      ...selectedExpenseProfile.settings,
      includePendingDepositsInOverdue,
      includeAvailableBalancesInOverdue,
    };
    void expensesProfilesDB.upsert({
      ...selectedExpenseProfile,
      settings: nextSettings,
    });
  }, [
    includeAvailableBalancesInOverdue,
    includePendingDepositsInOverdue,
    selectedExpenseProfile,
  ]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const remote = await expensesProfilesDB.getAll();
      if (!mounted) return;
      setExpenseProfiles(remote);
    };
    void load();
    const unsub = onExpensesProfilesChanged(() => void load());
    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  useEffect(() => {
    if (expenseProfiles.length === 0) return;

    const urlProfileId = searchParams.get(EXPENSES_PROFILE_PARAM_KEY);
    if (
      urlProfileId &&
      expenseProfiles.some((p) => p.id === urlProfileId) &&
      expenseProfileId !== urlProfileId
    ) {
      setExpenseProfileId(urlProfileId);
      return;
    }

    if (!expenseProfileId) {
      setExpenseProfileId(expenseProfiles[0]?.id || "");
      return;
    }
    if (expenseProfiles.some((p) => p.id === expenseProfileId)) return;
    setExpenseProfileId(expenseProfiles[0]?.id || "");
  }, [
    expenseProfiles,
    expenseProfileId,
    searchParams,
    EXPENSES_PROFILE_PARAM_KEY,
  ]);

  useEffect(() => {
    sessionStorage.setItem(
      "expenses_pinned_ids",
      JSON.stringify([...pinnedExpenseIds])
    );
  }, [pinnedExpenseIds]);

  // Income modal state
  const [incomeModalOpen, setIncomeModalOpen] = useState(false);
  const [incomeModalSection, setIncomeModalSection] =
    useState<ExpenseSection | null>(null);
  const [editingIncomeId, setEditingIncomeId] = useState<string | null>(null);
  const [editingExpectedDate, setEditingExpectedDate] = useState("");
  const [editingExpectedSaving, setEditingExpectedSaving] = useState(false);

  const handleSectionChange = useCallback(
    (section: ExpenseSection | null) => {
      setExpandedSection(section);
      if (section) {
        sessionStorage.setItem(EXPANDED_SECTION_STORAGE_KEY, section);
      } else {
        sessionStorage.removeItem(EXPANDED_SECTION_STORAGE_KEY);
      }
    },
    [EXPANDED_SECTION_STORAGE_KEY]
  );

  useEffect(() => {
    const highlightParam = searchParams.get("highlight");
    if (highlightParam) {
      console.debug("[ExpensesPage] Highlight requested", { highlightParam });
      setNewlyAddedExpenseId(highlightParam);
      setTimeout(() => setNewlyAddedExpenseId(null), 3000);
    }
    const shareParam = searchParams.get("share");
    if (shareParam === "1" && !pageShareOpen) {
      setPageShareOpen(true);
    }
    // Legacy deep link ?recurring=1 now maps to the Recurring Expenses tab.
    const recurringParam = searchParams.get("recurring");
    if (recurringParam === "1" && searchParams.get("view") !== "recurring") {
      const next = new URLSearchParams(searchParams);
      next.delete("recurring");
      next.set("view", "recurring");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, pageShareOpen, setSearchParams]);

  useEffect(() => {
    setShareHandler(() => {
      const next = new URLSearchParams(searchParams);
      next.set("share", "1");
      setSearchParams(next, { replace: true });
      setPageShareOpen(true);
    });
    return () => setShareHandler(null);
  }, [searchParams, setSearchParams, setShareHandler]);

  const handleSearchApply = useCallback((query: string) => {
    setAppliedSearchQuery(query);
    console.log("[ExpensesPage] Search applied:", query);
  }, []);

  // Undo functionality
  type UndoAction =
    | { type: "add"; expense: Expense }
    | { type: "update"; previousExpense: Expense; newExpense: Expense }
    | { type: "skip"; previousExpense: Expense }
    | { type: "paid"; previousExpense: Expense };
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);
  // `canUndo` gates the UNDO button in the bottom alert. Any action that both
  // pushes an entry onto `undoStack` and opens this snackbar should set it so
  // the alert offers a one-click revert (in addition to Ctrl/Cmd+Z).
  const [undoSnackbar, setUndoSnackbar] = useState<{
    open: boolean;
    message: string;
    canUndo?: boolean;
  }>({ open: false, message: "" });

  const onOpen = () => {
    setIsEditing(false);
    setCurrentExpense(undefined);
    setIsOpen(true);
  };

  const onClose = () => setIsOpen(false);

  const handleUpdateDebt = async (updatedDebt: Debt) => {
    try {
      const saved = await debtDB.updateDebt(updatedDebt);
      setDebts((prev) => prev.map((d) => (d.id === saved.id ? saved : d)));
      setLinkableDebts((prev) =>
        prev.map((d) => (d.id === saved.id ? saved : d))
      );
      console.log("[ExpensesPage] Debt updated from recurring view", saved.id);
    } catch (err) {
      console.error("[ExpensesPage] Failed to update debt", err);
    } finally {
      setDebtDrawerOpen(false);
      setCurrentDebt(undefined);
    }
  };

  const handleDeleteDebtById = async (debtId: string) => {
    const target = debts.find((d) => d.id === debtId);
    if (!target) return;
    if (
      !window.confirm(`Delete debt "${target.account}"? This cannot be undone.`)
    ) {
      return;
    }
    try {
      await debtDB.deleteDebt(debtId);
      setDebts((prev) => prev.filter((d) => d.id !== debtId));
      setLinkableDebts((prev) => prev.filter((d) => d.id !== debtId));
      console.log("[ExpensesPage] Debt deleted from recurring view", debtId);
    } catch (err) {
      console.error("[ExpensesPage] Failed to delete debt", err);
    }
  };

  // Set page title
  useEffect(() => {
    if (setPageTitle) {
      setPageTitle("Expenses");
    }
  }, [setPageTitle]);

  // Undo handler
  const handleUndo = useCallback(async () => {
    if (undoStack.length === 0) return;

    const lastAction = undoStack[undoStack.length - 1];
    try {
      if (lastAction.type === "add") {
        // Undo add: delete the expense
        await expenseAPI.delete(lastAction.expense.id);
        setExpenses((prev) =>
          prev.filter((e) => e.id !== lastAction.expense.id)
        );
        setUndoSnackbar({
          open: true,
          message: `Undone: "${lastAction.expense.name}" removed`,
        });
      } else if (lastAction.type === "update") {
        // Undo update: restore previous version
        const restored = await expenseAPI.update(lastAction.previousExpense);
        if (typeof restored.dueDate === "string") {
          restored.dueDate = new Date(restored.dueDate);
        }
        if (typeof restored.invoicedDate === "string") {
          restored.invoicedDate = new Date(restored.invoicedDate);
        }
        setExpenses((prev) =>
          prev.map((e) => (e.id === restored.id ? restored : e))
        );
        setUndoSnackbar({
          open: true,
          message: `Undone: "${lastAction.previousExpense.name}" restored`,
        });
      } else if (lastAction.type === "skip" || lastAction.type === "paid") {
        // Undo skip/paid: restore the pre-action version of the expense.
        const restored = await expenseAPI.update(lastAction.previousExpense);
        if (typeof restored.dueDate === "string") {
          restored.dueDate = new Date(restored.dueDate);
        }
        if (typeof restored.invoicedDate === "string") {
          restored.invoicedDate = new Date(restored.invoicedDate);
        }
        if (restored.paymentDate && typeof restored.paymentDate === "string") {
          restored.paymentDate = new Date(restored.paymentDate);
        }
        setExpenses((prev) =>
          prev.map((e) => (e.id === restored.id ? restored : e))
        );
        setUndoSnackbar({
          open: true,
          message: `Undone: "${lastAction.previousExpense.name}" restored`,
        });
      }
      // Remove the action from the stack
      setUndoStack((prev) => prev.slice(0, -1));
    } catch (error) {
      console.error("Failed to undo:", error);
      setUndoSnackbar({ open: true, message: "Failed to undo action" });
    }
  }, [undoStack]);

  // Keyboard listener for Ctrl+Z / Cmd+Z
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleUndo]);

  // Load expenses, income, and debts from database on initial render
  useEffect(() => {
    const loadData = async () => {
      const userId = getCachedUser()?.id || "unknown";
      const cacheKey = `expenses_page_cache_v2:${userId}`;
      localStorage.removeItem(`expenses_page_cache_v1:${userId}`);
      try {
        setIsLoading(true);
        setIsRefreshing(false);
        setLoadPerf((prev) => ({
          ...prev,
          cacheHydrated: false,
          cacheAgeMs: null,
          cacheParseMs: null,
          expensesFetchMs: null,
          incomeFetchMs: null,
          debtsFetchMs: null,
          networkTotalMs: null,
        }));

        // Hydrate immediately from last-known real data to avoid blocking first paint.
        const cachedRaw = localStorage.getItem(cacheKey);
        if (cachedRaw) {
          const cacheParseStart = performance.now();
          try {
            const cached = JSON.parse(cachedRaw) as {
              expenses?: Expense[];
              income?: Income[];
              debts?: Debt[];
              savedAt?: number;
            };

            if (
              Array.isArray(cached.expenses) &&
              Array.isArray(cached.income) &&
              Array.isArray(cached.debts)
            ) {
              const formattedExpenses = cached.expenses.map((expense) => {
                const formatted = { ...expense } as Expense;
                if (typeof formatted.dueDate === "string") {
                  formatted.dueDate = new Date(formatted.dueDate);
                }
                if (typeof formatted.invoicedDate === "string") {
                  formatted.invoicedDate = new Date(formatted.invoicedDate);
                }
                normalizeExpenseNumbers(formatted);
                return formatted;
              });

              const formattedIncome = cached.income.map((inc) => {
                const formatted = { ...inc } as Income;
                if (typeof formatted.expectedByDate === "string") {
                  formatted.expectedByDate = new Date(formatted.expectedByDate);
                }
                if (typeof formatted.invoiceTotal === "string") {
                  formatted.invoiceTotal = parseFloat(formatted.invoiceTotal);
                }
                if (typeof formatted.paidToDate === "string") {
                  formatted.paidToDate = parseFloat(formatted.paidToDate);
                }
                if (typeof formatted.receivedDate === "string") {
                  formatted.receivedDate = new Date(formatted.receivedDate);
                }
                if (typeof formatted.depositDate === "string") {
                  formatted.depositDate = new Date(formatted.depositDate);
                }
                return formatted;
              });

              setExpenses(formattedExpenses);
              setIncome(formattedIncome);
              setDebts(cached.debts);
              setLinkableDebts(cached.debts);

              const cacheAgeMs =
                typeof cached.savedAt === "number"
                  ? Math.max(0, Date.now() - cached.savedAt)
                  : null;
              setIsLoading(false);
              setIsRefreshing(true);
              setLoadPerf((prev) => ({
                ...prev,
                cacheHydrated: true,
                cacheAgeMs,
                cacheParseMs: Math.round(performance.now() - cacheParseStart),
              }));
              console.debug("[ExpensesPage] Hydrated from cache", {
                cacheKey,
                savedAt: cached.savedAt || null,
                expenses: formattedExpenses.length,
                income: formattedIncome.length,
                debts: cached.debts.length,
              });
            }
          } catch (error) {
            console.warn("[ExpensesPage] Failed to parse cache", {
              cacheKey,
              error,
            });
            setLoadPerf((prev) => ({
              ...prev,
              cacheHydrated: false,
              cacheParseMs: Math.round(performance.now() - cacheParseStart),
            }));
          }
        }

        const netStart = performance.now();
        const expensesPromise = (async () => {
          const t0 = performance.now();
          const result = await expenseAPI.getAll();
          setLoadPerf((prev) => ({
            ...prev,
            expensesFetchMs: Math.round(performance.now() - t0),
          }));
          return result;
        })();
        const incomePromise = (async () => {
          const t0 = performance.now();
          const result = await incomeAPI.getAll();
          setLoadPerf((prev) => ({
            ...prev,
            incomeFetchMs: Math.round(performance.now() - t0),
          }));
          return result;
        })();
        const debtsPromise = (async () => {
          const t0 = performance.now();
          const result = await debtDB.getAllDebts();
          setLoadPerf((prev) => ({
            ...prev,
            debtsFetchMs: Math.round(performance.now() - t0),
          }));
          return result;
        })();
        const bankAccountsPromise = plaidBankAccountService.getBankAccounts();

        const [allExpenses, allIncome, allDebts, allBankAccounts] =
          await Promise.all([
            expensesPromise,
            incomePromise,
            debtsPromise,
            bankAccountsPromise,
          ]);
        setLoadPerf((prev) => ({
          ...prev,
          networkTotalMs: Math.round(performance.now() - netStart),
        }));

        // Ensure all dueDates are Date objects and amounts are numbers
        const formattedExpenses = allExpenses.map((expense) => {
          const formatted = { ...expense };

          // Convert dueDate string to Date object preserving UTC
          if (typeof expense.dueDate === "string") {
            // Parse the UTC string directly to preserve the date
            const utcDate = new Date(expense.dueDate);
            formatted.dueDate = utcDate;
          }
          if (typeof expense.invoicedDate === "string") {
            formatted.invoicedDate = new Date(expense.invoicedDate);
          }

          normalizeExpenseNumbers(formatted);

          return formatted;
        });

        // Format income
        const formattedIncome = allIncome.map((inc) => {
          const formatted = { ...inc };
          if (typeof inc.expectedByDate === "string") {
            formatted.expectedByDate = new Date(inc.expectedByDate);
          }
          if (typeof inc.invoiceTotal === "string") {
            formatted.invoiceTotal = parseFloat(inc.invoiceTotal);
          }
          if (typeof inc.paidToDate === "string") {
            formatted.paidToDate = parseFloat(inc.paidToDate);
          }
          if (typeof inc.receivedDate === "string") {
            formatted.receivedDate = new Date(inc.receivedDate);
          }
          if (typeof inc.depositDate === "string") {
            formatted.depositDate = new Date(inc.depositDate);
          }
          return formatted;
        });

        logger.debug(
          logger.fmt`Loaded ${formattedExpenses.length} expenses, ${formattedIncome.length} income, and ${allDebts.length} debts`
        );
        setExpenses(formattedExpenses);
        setIncome(formattedIncome);
        setDebts(allDebts);
        setBankAccounts(allBankAccounts);
        setLinkableDebts(allDebts);

        try {
          const overrides = readPlaidDebtOverrides();
          const plaidDebts =
            await plaidCreditCardService.getCreditCardAccounts();
          const plaidDebtsWithOverrides = plaidDebts.map((debt) => {
            const plaidAccountId =
              "plaidAccountId" in debt
                ? (debt as any).plaidAccountId
                : undefined;
            const override =
              (plaidAccountId && overrides[plaidAccountId]) ||
              overrides[debt.id] ||
              null;
            return override ? ({ ...debt, ...override } as Debt) : debt;
          });
          setLinkableDebts([...allDebts, ...plaidDebtsWithOverrides]);
        } catch (error) {
          console.warn("[ExpensesPage] Failed to load Plaid debts for linker", {
            error,
          });
        }

        // Persist a compact last-known-good cache for fast first paint next time.
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              savedAt: Date.now(),
              expenses: formattedExpenses,
              income: formattedIncome,
              debts: allDebts,
            })
          );
        } catch (error) {
          console.warn("[ExpensesPage] Failed to write cache", {
            cacheKey,
            error,
          });
        }

        // Auto-run transaction analysis only in non-demo mode
        if (!demoService.isDemo()) {
          // Check if analysis was run recently (stored in localStorage)
          const lastAnalysisTime = localStorage.getItem(
            "lastTransactionAnalysis"
          );
          const now = Date.now();
          const sixHoursAgo = now - 6 * 60 * 60 * 1000; // 6 hours in milliseconds

          if (!lastAnalysisTime || parseInt(lastAnalysisTime) < sixHoursAgo) {
            // Run analysis after 2 seconds to allow UI to settle
            setTimeout(() => {
              console.log("Auto-running transaction analysis...");
              runTransactionAnalysis().then(() => {
                localStorage.setItem("lastTransactionAnalysis", now.toString());
              });
            }, 2000);
          } else {
            setLastAnalysis("Analysis skipped - ran recently");
          }
        }
      } catch (error) {
        captureErrorWithContext(error as Error, {
          component: "ExpensesPage",
          action: "loadData",
          context: "initial_load",
        });
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    };

    loadData();
  }, [readPlaidDebtOverrides]);

  useEffect(() => {
    let mounted = true;
    const refreshProfiles = async () => {
      const profiles = await taxProfilesDB.getAll();
      if (!mounted) return;
      setTaxProfiles(profiles);
      console.debug("[ExpensesPage] Loaded tax profiles", {
        count: profiles.length,
      });
    };
    void refreshProfiles();
    const unsub = onTaxProfilesChanged(() => void refreshProfiles());
    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  // Keep the local cache in sync with in-memory expense state so a refresh
  // never hydrates stale data. Debounce to avoid thrashing localStorage.
  useEffect(() => {
    if (isLoading) return;
    if (expenses.length === 0) return;
    const handle = window.setTimeout(() => {
      try {
        const userId = getCachedUser()?.id || "unknown";
        localStorage.setItem(
          `expenses_page_cache_v2:${userId}`,
          JSON.stringify({
            savedAt: Date.now(),
            expenses,
            income,
            debts,
          })
        );
      } catch (err) {
        console.debug("[ExpensesPage] live cache write failed", { err });
      }
    }, 500);
    return () => window.clearTimeout(handle);
  }, [expenses, income, debts, isLoading]);

  // Unification: every debt with a recurring amount must exist as a recurring
  // expense (so it shows on the Expenses page and the Recurring Expenses view).
  // For each such debt with no linked expense yet, create one and record the new
  // expense id on the debt (linkedExpenseId) so it is idempotent and never
  // respawns if the user later deletes it. Runs once after the initial load, and
  // covers future debts on subsequent loads.
  const debtExpenseSyncRan = useRef(false);
  useEffect(() => {
    if (debtExpenseSyncRan.current || isLoading || debts.length === 0) return;
    debtExpenseSyncRan.current = true;
    const linkedDebtIds = new Set(
      expenses.map((e) => e.linkedDebtId).filter(Boolean) as string[]
    );
    const missing = debts.filter(
      (d) =>
        Math.abs(Number(d.maxMonthlyPayment ?? 0)) > 0 &&
        !linkedDebtIds.has(d.id) &&
        !d.linkedExpenseId
    );
    if (missing.length === 0) return;
    (async () => {
      // Create all missing expenses in parallel so the one-time backfill is fast
      // (a serial loop over ~dozens of debts caused a multi-second delay).
      const results = await Promise.all(
        missing.map(async (d) => {
          try {
            const exp = await expenseAPI.add({
              name: d.creditor || d.account || "Debt",
              amount: Math.abs(Number(d.maxMonthlyPayment)),
              dueDate: new Date(),
              isPaid: false,
              isRecurring: true,
              recurrencePattern: "monthly",
              vendor: "",
              internalCategory: "",
              linkedDebtId: d.id,
              useLinkedDebtAmount: true,
              billOrBudget: "bill",
            } as Omit<Expense, "id">);
            normalizeExpenseNumbers(exp);
            if (typeof exp.dueDate === "string")
              exp.dueDate = new Date(exp.dueDate);
            // Record the link on the debt so this never respawns after a delete
            // (fire-and-forget; not needed to render the expense).
            debtDB
              .updateDebt({ ...d, linkedExpenseId: exp.id })
              .catch((linkErr) =>
                console.error(
                  "[ExpensesPage] Failed to record linkedExpenseId on debt",
                  d.id,
                  linkErr
                )
              );
            return exp;
          } catch (err) {
            console.error("[ExpensesPage] debt→expense sync failed", d.id, err);
            return null;
          }
        })
      );
      const created = results.filter(Boolean) as Expense[];
      if (created.length) {
        setExpenses((prev) => [...prev, ...created]);
        console.log(
          `[ExpensesPage] Auto-created ${created.length} recurring expenses for debts with recurring amounts`
        );
      }
    })();
  }, [isLoading, debts, expenses]);

  // Postpone (or un-postpone) a debt. `until` = ISO date string to postpone
  // until that date, null to postpone indefinitely, or "clear" to un-postpone.
  const handlePostponeDebt = async (
    debtId: string,
    until: string | null | "clear"
  ) => {
    const debt = debts.find((d) => d.id === debtId);
    if (!debt) return;
    const patch =
      until === "clear"
        ? { isPostponed: false, postponedUntil: null }
        : { isPostponed: true, postponedUntil: until };
    try {
      const updated = await debtDB.updateDebt({ ...debt, ...patch });
      setDebts((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      setLinkableDebts((prev) =>
        prev.map((d) => (d.id === updated.id ? updated : d))
      );
      console.log("[ExpensesPage] Postponed debt", { debtId, until });
    } catch (err) {
      console.error("[ExpensesPage] Failed to postpone debt", err);
    }
  };

  // Postpone (or un-postpone) an expense. `until` = ISO date string, null for
  // indefinite, or "clear" to un-postpone.
  const handlePostponeExpense = async (
    expenseId: string,
    until: string | null | "clear"
  ) => {
    const target = expenses.find((e) => e.id === expenseId);
    if (!target) return;
    const patch =
      until === "clear"
        ? { isPostponed: false, postponedUntil: null }
        : { isPostponed: true, postponedUntil: until };
    const updated = { ...target, ...patch };
    setExpenses((prev) => prev.map((e) => (e.id === expenseId ? updated : e)));
    try {
      await expenseAPI.update(updated);
    } catch (err) {
      console.error("[ExpensesPage] Failed to postpone expense", err);
    }
  };

  // Set (or clear) an expense's priority from the Recurring Expenses view.
  const handleSetExpensePriority = async (
    expenseId: string,
    priority: ExpensePriority | null
  ) => {
    const target = expenses.find((e) => e.id === expenseId);
    if (!target) return;
    const updated = { ...target, priority: priority || undefined };
    setExpenses((prev) => prev.map((e) => (e.id === expenseId ? updated : e)));
    try {
      await expenseAPI.update(updated);
    } catch (err) {
      console.error("[ExpensesPage] Failed to set expense priority", err);
    }
  };

  // Set (or clear) an expense's numeric priority rank from the Recurring
  // Expenses priority board. Multiple expenses may share a rank (a group).
  const handleSetExpenseRank = async (
    expenseId: string,
    rank: number | null
  ) => {
    const target = expenses.find((e) => e.id === expenseId);
    if (!target) return;
    if ((target.recurringPriorityRank ?? null) === rank) return;
    const updated = { ...target, recurringPriorityRank: rank };
    setExpenses((prev) => prev.map((e) => (e.id === expenseId ? updated : e)));
    try {
      await expenseAPI.update(updated);
    } catch (err) {
      console.error("[ExpensesPage] Failed to set expense rank", err);
    }
  };

  // Unify a debt and its linked expense: they are the same obligation.
  //  - If the debt already has a recurring amount, it DRIVES the expense amount.
  //  - If the debt has no recurring amount yet, adopt the expense's amount as the
  //    debt's recurring amount (so linking establishes the debt's amount).
  // Either way the expense is marked to use the linked debt's amount.
  const syncExpenseDebtLink = async <T extends Partial<Expense>>(
    exp: T
  ): Promise<T> => {
    const debtId = (exp as Partial<Expense>).linkedDebtId;
    if (!debtId) return exp;
    const debt = debts.find((d) => d.id === debtId);
    if (!debt) return exp;
    const debtAmount = Math.abs(Number(debt.maxMonthlyPayment ?? 0));
    if (debtAmount > 0) {
      return { ...exp, amount: debtAmount, useLinkedDebtAmount: true };
    }
    const expenseAmount = Math.abs(
      Number((exp as Partial<Expense>).amount ?? 0)
    );
    if (expenseAmount > 0) {
      try {
        const updated = await debtDB.updateDebt({
          ...debt,
          maxMonthlyPayment: expenseAmount,
        });
        setDebts((prev) =>
          prev.map((d) => (d.id === updated.id ? updated : d))
        );
        setLinkableDebts((prev) =>
          prev.map((d) => (d.id === updated.id ? updated : d))
        );
        console.log(
          "[ExpensesPage] Set debt recurring amount from linked expense",
          { debtId, expenseAmount }
        );
      } catch (err) {
        console.error("[ExpensesPage] Failed to back-fill debt amount", err);
      }
    }
    return { ...exp, useLinkedDebtAmount: true };
  };

  const handleAddExpense = async (
    newExpense: Omit<Expense, "id">
  ): Promise<void> => {
    newExpense = await syncExpenseDebtLink(newExpense);
    const resultExpense = await expenseAPI.add(newExpense);
    console.log("Added expense to database:", resultExpense);

    // Ensure the dueDate is a Date object
    if (typeof resultExpense.dueDate === "string") {
      resultExpense.dueDate = new Date(resultExpense.dueDate);
    }
    if (typeof resultExpense.invoicedDate === "string") {
      resultExpense.invoicedDate = new Date(resultExpense.invoicedDate);
    }
    normalizeExpenseNumbers(resultExpense);

    setExpenses([...expenses, resultExpense]);
    // Track for undo
    setUndoStack((prev) => [...prev, { type: "add", expense: resultExpense }]);
    setUndoSnackbar({
      open: true,
      message: `Added "${resultExpense.name}" - Press Ctrl+Z to undo`,
    });

    // Set newly added expense ID for pulse animation
    setNewlyAddedExpenseId(resultExpense.id);
    // Clear the highlight after 3 seconds
    setTimeout(() => setNewlyAddedExpenseId(null), 3000);

    onClose();
  };

  const handleUpdateExpense = async (
    updatedExpense: Expense
  ): Promise<void> => {
    updatedExpense = await syncExpenseDebtLink(updatedExpense);
    // Projected expenses (future recurring instances) don't exist in the DB yet.
    // We need to create a new real instance instead of updating.
    if (updatedExpense.id.startsWith("projected-")) {
      console.debug(
        "[ExpensesPage] Converting projected expense to real instance:",
        {
          projectedId: updatedExpense.id,
          originalId: updatedExpense.originalId,
          name: updatedExpense.name,
        }
      );

      const { id: _projectedId, ...expenseWithoutId } = updatedExpense;
      const newExpenseData: Omit<Expense, "id"> = {
        ...expenseWithoutId,
        isRecurringInstance: true,
        isRecurring: false,
        originalId: updatedExpense.originalId || updatedExpense.id,
      };

      const resultExpense = await expenseAPI.add(newExpenseData);
      console.log(
        "Created real instance from projected expense:",
        resultExpense
      );

      // Ensure date/amount types
      if (typeof resultExpense.dueDate === "string") {
        resultExpense.dueDate = new Date(resultExpense.dueDate);
      }
      if (typeof resultExpense.invoicedDate === "string") {
        resultExpense.invoicedDate = new Date(resultExpense.invoicedDate);
      }
      normalizeExpenseNumbers(resultExpense);

      // Replace the projected entry with the real one in state
      setExpenses((prev) => {
        const withoutProjected = prev.filter((e) => e.id !== updatedExpense.id);
        return [...withoutProjected, resultExpense];
      });

      setUndoSnackbar({
        open: true,
        message: `Created instance of "${resultExpense.name}"`,
      });
      onClose();
      return;
    }

    // Store the previous version for undo
    const previousExpense = expenses.find((e) => e.id === updatedExpense.id);
    const requestRecurringEditScope = (expense: Expense) =>
      new Promise<RecurringEditScope | null>((resolve) => {
        recurringEditResolverRef.current = resolve;
        setRecurringEditTarget(expense);
        setRecurringEditDialogOpen(true);
      });

    const applyDefinitionFields = (
      targetExpense: Expense,
      sourceExpense: Expense
    ): Expense => ({
      ...targetExpense,
      name: sourceExpense.name,
      amount: sourceExpense.amount,
      vendor: sourceExpense.vendor,
      internalCategory: sourceExpense.internalCategory,
      notes: sourceExpense.notes,
      linkedDebtId: sourceExpense.linkedDebtId,
      useLinkedDebtAmount: sourceExpense.useLinkedDebtAmount,
      billOrBudget: sourceExpense.billOrBudget,
      calculationType: sourceExpense.calculationType,
      percentOfIncome: sourceExpense.percentOfIncome,
      incomeBasis: sourceExpense.incomeBasis,
      taxProfileId: sourceExpense.taxProfileId,
      taxProfileName: sourceExpense.taxProfileName,
      expensesProfileId: sourceExpense.expensesProfileId,
      suspensionPeriodValue: sourceExpense.suspensionPeriodValue,
      suspensionPeriodUnit: sourceExpense.suspensionPeriodUnit,
      isRecurring: targetExpense.isRecurring,
      recurrencePattern:
        sourceExpense.recurrencePattern || targetExpense.recurrencePattern,
    });

    let forceSingleOccurrenceUpdate = false;
    if (
      previousExpense &&
      isRecurringSeriesExpense(previousExpense) &&
      didAmountChange(previousExpense, updatedExpense)
    ) {
      const scope = await requestRecurringEditScope(previousExpense);
      if (!scope) return;

      if (scope === "this_and_future") {
        const toDayStartMs = (
          value: Date | string | undefined
        ): number | null => {
          if (!value) return null;
          const date =
            value instanceof Date ? new Date(value) : new Date(value);
          if (Number.isNaN(date.getTime())) return null;
          date.setHours(0, 0, 0, 0);
          return date.getTime();
        };

        const seriesRootId = getExpenseSeriesRootId(previousExpense);
        const selectedDueMs = toDayStartMs(previousExpense.dueDate);
        const futureItems = expenses.filter((item) => {
          const inSeries =
            item.id === seriesRootId || item.originalId === seriesRootId;
          if (!inSeries) return false;
          const dueMs = toDayStartMs(item.dueDate);
          return selectedDueMs !== null && dueMs !== null
            ? dueMs >= selectedDueMs
            : false;
        });

        const updatedItems = await Promise.all(
          futureItems.map(async (item) => {
            const result = await expenseAPI.update(
              applyDefinitionFields(item, updatedExpense)
            );
            if (typeof result.dueDate === "string") {
              result.dueDate = new Date(result.dueDate);
            }
            if (typeof result.invoicedDate === "string") {
              result.invoicedDate = new Date(result.invoicedDate);
            }
            normalizeExpenseNumbers(result);
            return result;
          })
        );

        const updatedById = new Map(
          updatedItems.map((item) => [item.id, item])
        );
        setExpenses((prev) =>
          prev.map((item) =>
            updatedById.has(item.id) ? updatedById.get(item.id)! : item
          )
        );

        setUndoSnackbar({
          open: true,
          message: `Updated "${updatedExpense.name}" for this and future instances`,
        });
        onClose();
        return;
      }

      forceSingleOccurrenceUpdate = true;
    }

    // Editing a real recurring instance and changing its due date should *not* move the
    // existing instance (which looks like deletion of the old month). Instead:
    // - create a new instance at the new date (or update an existing instance already on that date)
    // - keep the original instance row intact
    if (
      previousExpense &&
      isSeriesOccurrence(previousExpense) &&
      didDueDateDayChange(previousExpense, updatedExpense)
    ) {
      const seriesRootId = getExpenseSeriesRootId(previousExpense);
      const targetDueDate = updatedExpense.dueDate;
      const existingOnTarget = findSeriesOccurrenceByDueDate(
        expenses.filter((e) => e.id !== previousExpense.id),
        seriesRootId,
        targetDueDate
      );

      if (existingOnTarget) {
        const resultExpense = await expenseAPI.update({
          ...existingOnTarget,
          ...updatedExpense,
          id: existingOnTarget.id,
          // Preserve series metadata.
          isRecurring: false,
          isRecurringInstance: true,
          originalId: seriesRootId,
        });

        if (typeof resultExpense.dueDate === "string") {
          resultExpense.dueDate = new Date(resultExpense.dueDate);
        }
        if (typeof resultExpense.invoicedDate === "string") {
          resultExpense.invoicedDate = new Date(resultExpense.invoicedDate);
        }
        normalizeExpenseNumbers(resultExpense);

        setExpenses((prev) =>
          prev.map((e) => (e.id === resultExpense.id ? resultExpense : e))
        );
        setUndoSnackbar({
          open: true,
          message: `Updated existing occurrence for "${resultExpense.name}" without moving the original`,
        });
        onClose();
        return;
      }

      const newInstanceData: Omit<Expense, "id"> = {
        ...previousExpense,
        ...updatedExpense,
        // Force as a real instance tied to the series root.
        isRecurring: false,
        isRecurringInstance: true,
        originalId: seriesRootId,
      };

      const created = await expenseAPI.add(newInstanceData);
      if (typeof created.dueDate === "string") {
        created.dueDate = new Date(created.dueDate);
      }
      if (typeof created.invoicedDate === "string") {
        created.invoicedDate = new Date(created.invoicedDate);
      }
      normalizeExpenseNumbers(created);

      setExpenses((prev) => [...prev, created]);
      setUndoSnackbar({
        open: true,
        message: `Created new occurrence for "${created.name}" and kept the original`,
      });
      onClose();
      return;
    }

    const shouldApplyToSeries = Boolean(
      !forceSingleOccurrenceUpdate &&
      (updatedExpense.isRecurring ||
        updatedExpense.isRecurringInstance ||
        updatedExpense.originalId)
    );
    const resultExpense = await expenseAPI.update({
      ...updatedExpense,
      applyToSeries: shouldApplyToSeries,
    });
    console.log("Updated expense in database:", resultExpense);

    // Ensure the dueDate is a Date object
    if (typeof resultExpense.dueDate === "string") {
      resultExpense.dueDate = new Date(resultExpense.dueDate);
    }
    if (typeof resultExpense.invoicedDate === "string") {
      resultExpense.invoicedDate = new Date(resultExpense.invoicedDate);
    }
    normalizeExpenseNumbers(resultExpense);

    const seriesRootId = resultExpense.originalId || resultExpense.id;
    if (shouldApplyToSeries) {
      // Mirror the backend's series update behavior locally so the UI reflects
      // changes immediately without requiring a refresh.
      setExpenses((prev) =>
        prev.map((item) => {
          const inSeries =
            item.id === seriesRootId || item.originalId === seriesRootId;
          if (!inSeries) return item;
          if (item.id === resultExpense.id) return resultExpense;

          // Keep per-occurrence state, override definition-level fields.
          return {
            ...item,
            name: resultExpense.name,
            amount: resultExpense.amount,
            vendor: resultExpense.vendor,
            internalCategory: resultExpense.internalCategory,
            notes: resultExpense.notes,
            isRecurring: resultExpense.isRecurring,
            recurrencePattern: resultExpense.recurrencePattern,
            priority: resultExpense.priority,
            isPostponed: resultExpense.isPostponed,
            linkedDebtId: resultExpense.linkedDebtId,
            useLinkedDebtAmount: resultExpense.useLinkedDebtAmount,
            suspensionPeriodValue: resultExpense.suspensionPeriodValue,
            suspensionPeriodUnit: resultExpense.suspensionPeriodUnit,
            billOrBudget: resultExpense.billOrBudget,
            linkedShoppingListId: resultExpense.linkedShoppingListId,
            calculationType: resultExpense.calculationType,
            percentOfIncome: resultExpense.percentOfIncome,
            incomeBasis: resultExpense.incomeBasis,
            taxProfileId: resultExpense.taxProfileId,
            taxProfileName: resultExpense.taxProfileName,
            expensesProfileId: resultExpense.expensesProfileId,
          } as Expense;
        })
      );

      // Backfill missing instances from today forward.
      try {
        const regen = await expenseAPI.regenerateInstances(seriesRootId, 24);
        if (regen.instancesCreated > 0 && regen.instances.length > 0) {
          const normalized = regen.instances.map((inst) => {
            const next = { ...inst } as Expense;
            if (typeof next.dueDate === "string") {
              next.dueDate = new Date(next.dueDate);
            }
            if (typeof next.invoicedDate === "string")
              next.invoicedDate = new Date(next.invoicedDate);
            normalizeExpenseNumbers(next);
            return next;
          });
          setExpenses((prev) => {
            const existingIds = new Set(prev.map((e) => e.id));
            const additions = normalized.filter((e) => !existingIds.has(e.id));
            return additions.length ? [...prev, ...additions] : prev;
          });
        }
      } catch (error) {
        console.warn(
          "[ExpensesPage] Failed to regenerate instances after update",
          {
            seriesRootId,
            error,
          }
        );
      }
    } else {
      setExpenses((prev) =>
        prev.map((expense) =>
          expense.id === resultExpense.id ? resultExpense : expense
        )
      );
    }

    // Track for undo (only if we found the previous version)
    if (previousExpense) {
      setUndoStack((prev) => [
        ...prev,
        { type: "update", previousExpense, newExpense: resultExpense },
      ]);
      setUndoSnackbar({
        open: true,
        message: `Updated "${resultExpense.name}" - Press Ctrl+Z to undo`,
      });
    }
    onClose();
  };

  const handleRescheduleExpense = async (
    expense: Expense,
    newDate: Date,
    scope: "single" | "all_future" = "single"
  ): Promise<void> => {
    const previousExpense =
      expenses.find((e) => e.id === expense.id) || expense;
    const selectedDueDate = new Date(expense.dueDate);
    const shiftMs = newDate.getTime() - selectedDueDate.getTime();

    const toDayStartMs = (value: Date | string | undefined): number | null => {
      if (!value) return null;
      const date = value instanceof Date ? new Date(value) : new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      date.setHours(0, 0, 0, 0);
      return date.getTime();
    };

    console.log("[ExpensesPage] Rescheduling expense:", {
      expenseId: expense.id,
      previousDueDate: expense.dueDate,
      nextDueDate: newDate,
      scope,
    });
    try {
      if (scope === "all_future") {
        const baseId = expense.originalId || expense.id;
        const selectedDueMs = toDayStartMs(expense.dueDate);
        const seriesItems = expenses.filter(
          (item) => item.id === baseId || item.originalId === baseId
        );
        const futureItems = seriesItems.filter((item) => {
          const itemDueMs = toDayStartMs(item.dueDate);
          return itemDueMs !== null && selectedDueMs !== null
            ? itemDueMs >= selectedDueMs
            : false;
        });

        if (futureItems.length === 0) {
          throw new Error("No future recurring instances found to reschedule");
        }

        const updatedItems: Expense[] = [];
        for (const item of futureItems) {
          const shiftedExpense: Expense = {
            ...item,
            dueDate: new Date(new Date(item.dueDate).getTime() + shiftMs),
          };
          if (item.invoicedDate) {
            shiftedExpense.invoicedDate = new Date(
              new Date(item.invoicedDate).getTime() + shiftMs
            );
          }
          const resultExpense = await expenseAPI.update(shiftedExpense);
          if (typeof resultExpense.dueDate === "string") {
            resultExpense.dueDate = new Date(resultExpense.dueDate);
          }
          if (typeof resultExpense.invoicedDate === "string") {
            resultExpense.invoicedDate = new Date(resultExpense.invoicedDate);
          }
          normalizeExpenseNumbers(resultExpense);
          updatedItems.push(resultExpense);
        }

        const updatedById = new Map(
          updatedItems.map((item) => [item.id, item])
        );
        setExpenses((prev) =>
          prev.map((item) =>
            updatedById.has(item.id) ? updatedById.get(item.id)! : item
          )
        );
      } else {
        const updatedExpense: Expense = {
          ...expense,
          dueDate: newDate,
        };
        if (expense.invoicedDate) {
          const currentInvoicedDate = new Date(expense.invoicedDate);
          updatedExpense.invoicedDate = new Date(
            currentInvoicedDate.getTime() + shiftMs
          );
          console.log("[ExpensesPage] Adjusted invoiced date on reschedule:", {
            expenseId: expense.id,
            previousInvoicedDate: expense.invoicedDate,
            nextInvoicedDate: updatedExpense.invoicedDate,
          });
        }

        const resultExpense = await expenseAPI.update(updatedExpense);
        console.log("[ExpensesPage] Reschedule update result:", resultExpense);

        if (typeof resultExpense.dueDate === "string") {
          resultExpense.dueDate = new Date(resultExpense.dueDate);
        }
        if (typeof resultExpense.invoicedDate === "string") {
          resultExpense.invoicedDate = new Date(resultExpense.invoicedDate);
        }
        normalizeExpenseNumbers(resultExpense);

        setExpenses((prev) =>
          prev.map((item) =>
            item.id === resultExpense.id ? resultExpense : item
          )
        );
      }

      setUndoStack((prev) => [
        ...prev,
        {
          type: "update",
          previousExpense,
          newExpense: { ...expense, dueDate: newDate },
        },
      ]);
      setUndoSnackbar({
        open: true,
        message:
          scope === "all_future"
            ? `Rescheduled this and future "${expense.name}" items - Press Ctrl+Z to undo`
            : `Rescheduled "${expense.name}" - Press Ctrl+Z to undo`,
      });
    } catch (error) {
      console.error("[ExpensesPage] Failed to reschedule expense:", error);
      setUndoSnackbar({ open: true, message: "Failed to reschedule expense" });
      throw error;
    }
  };

  const handleEditExpense = (expense: Expense) => {
    setCurrentExpense(expense);
    setIsEditing(true);
    setIsOpen(true);
  };

  const closeIncomeDrawer = () => {
    console.log("[ExpensesPage] Closing income editor");
    setIncomeDrawerOpen(false);
    setIsIncomeEditing(false);
    setCurrentIncome(null);
  };

  const handleAddIncome = async (
    incomeData: Omit<Income, "id">
  ): Promise<void> => {
    try {
      const newIncome = await incomeAPI.add(incomeData);
      if (typeof newIncome.expectedByDate === "string") {
        newIncome.expectedByDate = new Date(newIncome.expectedByDate);
      }
      if (typeof newIncome.invoiceTotal === "string") {
        newIncome.invoiceTotal = parseFloat(newIncome.invoiceTotal);
      }
      if (typeof newIncome.paidToDate === "string") {
        newIncome.paidToDate = parseFloat(newIncome.paidToDate);
      }
      if (typeof newIncome.receivedDate === "string") {
        newIncome.receivedDate = new Date(newIncome.receivedDate);
      }
      if (typeof newIncome.depositDate === "string") {
        newIncome.depositDate = new Date(newIncome.depositDate);
      }
      console.log("[ExpensesPage] Added income:", newIncome);
      setIncome((prev) => [...prev, newIncome]);
      closeIncomeDrawer();
    } catch (error) {
      console.error("[ExpensesPage] Failed to add income:", error);
      alert("Failed to save income. Please try again.");
    }
  };

  const handleUpdateIncome = async (updatedIncome: Income): Promise<void> => {
    try {
      console.log("[ExpensesPage] Updating income:", updatedIncome);
      const resultIncome = await incomeAPI.update(updatedIncome);
      if (typeof resultIncome.expectedByDate === "string") {
        resultIncome.expectedByDate = new Date(resultIncome.expectedByDate);
      }
      if (typeof resultIncome.invoiceTotal === "string") {
        resultIncome.invoiceTotal = parseFloat(resultIncome.invoiceTotal);
      }
      if (typeof resultIncome.paidToDate === "string") {
        resultIncome.paidToDate = parseFloat(resultIncome.paidToDate);
      }
      if (typeof resultIncome.receivedDate === "string") {
        resultIncome.receivedDate = new Date(resultIncome.receivedDate);
      }
      if (typeof resultIncome.depositDate === "string") {
        resultIncome.depositDate = new Date(resultIncome.depositDate);
      }
      console.log("[ExpensesPage] Updated income result:", resultIncome);
      setIncome((prevIncome) =>
        prevIncome.map((item) =>
          item.id === resultIncome.id ? resultIncome : item
        )
      );
      closeIncomeDrawer();
    } catch (error) {
      console.error("[ExpensesPage] Failed to update income:", error);
      alert("Failed to update income. Please try again.");
    }
  };

  const handleDeleteExpense = async (expense: Expense) => {
    const requestRecurringDeleteScope = () =>
      new Promise<RecurringDeleteScope | null>((resolve) => {
        recurringDeleteResolverRef.current = resolve;
        setRecurringDeleteTarget(expense);
        setRecurringDeleteDialogOpen(true);
      });

    const toDayStartMs = (value: Date | string | undefined): number | null => {
      if (!value) return null;
      const date = value instanceof Date ? new Date(value) : new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      date.setHours(0, 0, 0, 0);
      return date.getTime();
    };

    const createSkippedInstanceForProjected = async (projected: Expense) => {
      const originalId = projected.originalId;
      if (!originalId) {
        throw new Error("Projected expense missing originalId");
      }

      console.debug(
        "[ExpensesPage] Deleting projected expense by creating skipped instance",
        {
          projectedId: projected.id,
          originalId,
          dueDate:
            projected.dueDate instanceof Date
              ? projected.dueDate.toISOString()
              : projected.dueDate,
          name: projected.name,
        }
      );

      const { id: _projectedId, ...expenseWithoutId } = projected;
      const newExpenseData: Omit<Expense, "id"> = {
        ...expenseWithoutId,
        isRecurringInstance: true,
        isRecurring: false,
        isSkipped: true,
        originalId,
      };

      const resultExpense = await expenseAPI.add(newExpenseData);
      console.log(
        "[ExpensesPage] Created skipped instance to suppress projection:",
        resultExpense.id
      );

      if (typeof resultExpense.dueDate === "string") {
        resultExpense.dueDate = new Date(resultExpense.dueDate);
      }
      if (typeof resultExpense.invoicedDate === "string") {
        resultExpense.invoicedDate = new Date(resultExpense.invoicedDate);
      }
      normalizeExpenseNumbers(resultExpense);

      setExpenses((prev) => [...prev, resultExpense]);
      setUndoSnackbar({
        open: true,
        message: `Removed projected "${projected.name}" for that date`,
      });
    };

    try {
      const isRecurringSeriesItem =
        expense.isRecurring ||
        Boolean(expense.isRecurringInstance) ||
        Boolean(expense.originalId);

      if (isRecurringSeriesItem) {
        const scope = await requestRecurringDeleteScope();
        if (!scope) {
          throw new Error("DELETE_CANCELLED");
        }

        const baseId = expense.originalId || expense.id;
        const selectedDueMs = toDayStartMs(expense.dueDate);

        // Use explicit matching instead of `(item.originalId || item.id)` so
        // legacy/empty originalId values don't break series selection.
        const seriesExpenses = expenses.filter(
          (item) => item.id === baseId || item.originalId === baseId
        );
        const seriesRoot = expenses.find((item) => item.id === baseId) || null;

        const shouldDeleteByScope = (item: Expense) => {
          if (!selectedDueMs) return scope === "all_past_present_future";
          const itemDueMs = toDayStartMs(item.dueDate);
          if (!itemDueMs) return false;
          if (scope === "all_past_present_future") return true;
          if (scope === "just_this") return item.id === expense.id;
          if (scope === "this_and_future") return itemDueMs >= selectedDueMs;
          if (scope === "this_and_past") return itemDueMs <= selectedDueMs;
          return false;
        };

        const expensesToDelete = seriesExpenses.filter(shouldDeleteByScope);

        // For projected occurrences, there is no DB row; convert deletion into skipped instance.
        if (
          expense.id.startsWith("projected-") &&
          (scope === "just_this" || expensesToDelete.length === 0)
        ) {
          await createSkippedInstanceForProjected(expense);
          return;
        }

        if (expensesToDelete.length === 0) {
          throw new Error(
            "No recurring instances matched selected delete scope"
          );
        }

        await Promise.all(
          expensesToDelete.map((item) => expenseAPI.delete(item.id))
        );
        const deletedIds = new Set(expensesToDelete.map((item) => item.id));
        setExpenses((prev) => prev.filter((item) => !deletedIds.has(item.id)));

        // If the user deletes "this and future", but the series root is earlier than the selected
        // occurrence, we must disable recurrence on the root; otherwise projections will keep
        // reappearing even after deleting instance rows.
        if (scope === "this_and_future" && seriesRoot && selectedDueMs) {
          const rootDueMs = toDayStartMs(seriesRoot.dueDate);
          const rootIsBeforeSelected =
            rootDueMs !== null ? rootDueMs < selectedDueMs : true;
          if (rootIsBeforeSelected) {
            console.debug(
              "[ExpensesPage] Disabling recurrence on series root",
              {
                baseId,
                rootId: seriesRoot.id,
                selectedExpenseId: expense.id,
                scope,
              }
            );
            const updatedRoot = await expenseAPI.update({
              ...seriesRoot,
              isRecurring: false,
            });
            if (typeof updatedRoot.dueDate === "string") {
              updatedRoot.dueDate = new Date(updatedRoot.dueDate);
            }
            if (typeof updatedRoot.invoicedDate === "string") {
              updatedRoot.invoicedDate = new Date(updatedRoot.invoicedDate);
            }
            normalizeExpenseNumbers(updatedRoot);
            setExpenses((prev) =>
              prev.map((item) =>
                item.id === updatedRoot.id ? updatedRoot : item
              )
            );
          }
        }

        setUndoSnackbar({
          open: true,
          message:
            expensesToDelete.length === 1
              ? `Deleted "${expense.name}"`
              : `Deleted ${expensesToDelete.length} recurring expense instances`,
        });
        return;
      }

      // Projected expenses (future recurring instances) don't exist in the DB yet.
      // "Deleting" them should suppress the projection for that due date by creating
      // a real skipped instance tied to the original recurring expense.
      if (expense.id.startsWith("projected-")) {
        await createSkippedInstanceForProjected(expense);
        return;
      }

      await expenseAPI.delete(expense.id);
      console.log("Deleted expense from database:", expense.id);
      setExpenses((prev) => prev.filter((e) => e.id !== expense.id));
    } catch (error) {
      if (error instanceof Error && error.message === "DELETE_CANCELLED") {
        throw error;
      }
      console.error("[ExpensesPage] Failed to delete expense:", {
        id: expense?.id,
        name: expense?.name,
        error,
      });
      alert("Failed to delete expense. Check console for details.");
    }
  };

  const handleStopRecurringAfterDate = async (expense: Expense) => {
    const toDayStartMs = (value: Date | string | undefined): number | null => {
      if (!value) return null;
      const date = value instanceof Date ? new Date(value) : new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      date.setHours(0, 0, 0, 0);
      return date.getTime();
    };

    try {
      const baseId = expense.originalId || expense.id;
      const selectedDueMs = toDayStartMs(expense.dueDate);
      const seriesRoot = expenses.find((item) => item.id === baseId) || null;
      const futureExpenses = getFutureExpenseSeriesOccurrences(
        expenses,
        expense
      );

      console.debug("[ExpensesPage] Stop recurring after date", {
        expenseId: expense.id,
        baseId,
        dueDate: expense.dueDate,
        futureCount: futureExpenses.length,
      });

      if (futureExpenses.length > 0) {
        await Promise.all(
          futureExpenses.map((item) => expenseAPI.delete(item.id))
        );
      }

      const deletedIds = new Set(futureExpenses.map((item) => item.id));
      if (seriesRoot) {
        const rootDueMs = toDayStartMs(seriesRoot.dueDate);
        const shouldDisableRootRecurrence =
          selectedDueMs !== null
            ? rootDueMs === null || rootDueMs <= selectedDueMs
            : true;

        if (shouldDisableRootRecurrence && seriesRoot.isRecurring) {
          const updatedRoot = await expenseAPI.update({
            ...seriesRoot,
            isRecurring: false,
          });
          if (typeof updatedRoot.dueDate === "string") {
            updatedRoot.dueDate = new Date(updatedRoot.dueDate);
          }
          if (typeof updatedRoot.invoicedDate === "string") {
            updatedRoot.invoicedDate = new Date(updatedRoot.invoicedDate);
          }
          normalizeExpenseNumbers(updatedRoot);

          setExpenses((prev) =>
            prev
              .filter((item) => !deletedIds.has(item.id))
              .map((item) => (item.id === updatedRoot.id ? updatedRoot : item))
          );
        } else {
          setExpenses((prev) =>
            prev.filter((item) => !deletedIds.has(item.id))
          );
        }
      } else {
        setExpenses((prev) => prev.filter((item) => !deletedIds.has(item.id)));
      }

      setUndoSnackbar({
        open: true,
        message:
          futureExpenses.length > 0
            ? `Stopped "${expense.name}" after ${new Date(
                expense.dueDate
              ).toLocaleDateString("en-US", { timeZone: "UTC" })}`
            : `No future "${expense.name}" instances remained`,
      });
    } catch (error) {
      console.error("[ExpensesPage] Failed to stop recurring after date", {
        expenseId: expense.id,
        error,
      });
      throw error;
    }
  };

  const handleMergeExpenseIntoSeries = async (
    expense: Expense,
    seriesRoot: Expense
  ) => {
    try {
      console.debug("[ExpensesPage] Merge expense into recurring series", {
        expenseId: expense.id,
        seriesRootId: seriesRoot.id,
      });

      const resultExpense = await expenseAPI.update({
        ...expense,
        name: seriesRoot.name,
        vendor: seriesRoot.vendor,
        internalCategory: seriesRoot.internalCategory,
        notes: expense.notes || seriesRoot.notes,
        linkedDebtId: seriesRoot.linkedDebtId,
        useLinkedDebtAmount: seriesRoot.useLinkedDebtAmount,
        billOrBudget: seriesRoot.billOrBudget,
        calculationType: seriesRoot.calculationType,
        percentOfIncome: seriesRoot.percentOfIncome,
        incomeBasis: seriesRoot.incomeBasis,
        taxProfileId: seriesRoot.taxProfileId,
        taxProfileName: seriesRoot.taxProfileName,
        expensesProfileId: seriesRoot.expensesProfileId,
        suspensionPeriodValue: seriesRoot.suspensionPeriodValue,
        suspensionPeriodUnit: seriesRoot.suspensionPeriodUnit,
        isRecurring: false,
        isRecurringInstance: true,
        originalId: seriesRoot.id,
        recurrencePattern: seriesRoot.recurrencePattern,
      });

      if (typeof resultExpense.dueDate === "string") {
        resultExpense.dueDate = new Date(resultExpense.dueDate);
      }
      if (typeof resultExpense.invoicedDate === "string") {
        resultExpense.invoicedDate = new Date(resultExpense.invoicedDate);
      }
      normalizeExpenseNumbers(resultExpense);

      setExpenses((prev) =>
        prev.map((item) =>
          item.id === resultExpense.id ? resultExpense : item
        )
      );
      setUndoSnackbar({
        open: true,
        message: `Merged "${expense.name}" into "${seriesRoot.name}"`,
      });
    } catch (error) {
      console.error("[ExpensesPage] Failed to merge expense into series", {
        expenseId: expense.id,
        seriesRootId: seriesRoot.id,
        error,
      });
      throw error;
    }
  };

  const handleSkipExpense = async (expense: Expense) => {
    try {
      const updatedExpense = {
        ...expense,
        isSkipped: !expense.isSkipped, // Toggle skip status
      };

      const resultExpense = await expenseAPI.update(updatedExpense);
      console.log(
        `[ExpensesPage] Expense ${expense.isSkipped ? "unskipped" : "skipped"}:`,
        resultExpense.id
      );

      // Ensure dueDate is properly converted
      if (typeof resultExpense.dueDate === "string") {
        resultExpense.dueDate = new Date(resultExpense.dueDate);
      }
      if (typeof resultExpense.invoicedDate === "string") {
        resultExpense.invoicedDate = new Date(resultExpense.invoicedDate);
      }

      // Update local state
      setExpenses((prevExpenses) =>
        prevExpenses.map((exp) =>
          exp.id === resultExpense.id ? resultExpense : exp
        )
      );

      // Record for undo (Ctrl/Cmd+Z and the bottom-alert UNDO button). The
      // pre-toggle `expense` is the version to restore.
      setUndoStack((prev) => [
        ...prev,
        { type: "skip", previousExpense: expense },
      ]);
      setUndoSnackbar({
        open: true,
        message: `${expense.isSkipped ? "Unskipped" : "Skipped"} "${expense.name}"`,
        canUndo: true,
      });
    } catch (error) {
      console.error("Failed to skip expense:", error);
      setUndoSnackbar({ open: true, message: "Failed to skip expense" });
      throw error;
    }
  };

  const handleDuplicateExpense = async (expense: Expense) => {
    // Create a copy without the id (will be generated by the API)
    const duplicatedExpense: Omit<Expense, "id"> = {
      name: `${expense.name} (Copy)`,
      amount: expense.amount,
      invoicedDate: expense.invoicedDate,
      dueDate: expense.dueDate,
      isPaid: false, // Reset paid status for the copy
      isRecurring: expense.isRecurring,
      recurrencePattern: expense.recurrencePattern,
      vendor: expense.vendor,
      internalCategory: expense.internalCategory,
      notes: expense.notes,
      linkedDebtId: expense.linkedDebtId,
      useLinkedDebtAmount: expense.useLinkedDebtAmount,
      paymentAccount: expense.paymentAccount,
    };

    try {
      const newExpense = await expenseAPI.add(duplicatedExpense);
      setExpenses([...expenses, newExpense]);
      console.log("Duplicated expense:", newExpense.id);
    } catch (error) {
      console.error("Failed to duplicate expense:", error);
      captureErrorWithContext(error as Error, {
        action: "duplicateExpense",
        originalExpenseId: expense.id,
      });
    }
  };

  const handleShareExpense = async (expense: Expense) => {
    setShareExpense(expense);
    setShareOpen(true);

    if (groups.length > 0) {
      return;
    }

    try {
      const availableGroups = await groupAPI.list();
      setGroups(availableGroups);
    } catch (error) {
      console.error("Failed to load groups:", error);
    }
  };

  const handleShareRecord = (
    groupIds: string[],
    permission: "view" | "edit"
  ) => {
    if (!shareExpense) {
      return;
    }

    void (async () => {
      try {
        await shareAPI.shareRecord({
          recordType: "expense",
          recordId: shareExpense.id,
          groupIds,
          permission,
        });
      } catch (error) {
        console.error("Failed to share expense:", error);
        alert("Failed to share expense. Please try again.");
      }
    })();
  };

  const handleMarkAsPaid = async (
    expense: Expense,
    paymentDate: Date,
    paymentAccount: string,
    linkedTransactionId?: string
  ) => {
    try {
      const updatedExpense: Expense = {
        ...expense,
        isPaid: true,
        paymentDate,
        paymentAccount: paymentAccount || undefined,
        linkedTransactionId,
      };

      // Update in database
      const resultExpense = await expenseAPI.update(updatedExpense);

      console.log("Marked expense as paid:", resultExpense);

      // Ensure the dueDate and paymentDate are Date objects
      if (typeof resultExpense.dueDate === "string") {
        resultExpense.dueDate = new Date(resultExpense.dueDate);
      }
      if (typeof resultExpense.invoicedDate === "string") {
        resultExpense.invoicedDate = new Date(resultExpense.invoicedDate);
      }
      if (
        resultExpense.paymentDate &&
        typeof resultExpense.paymentDate === "string"
      ) {
        resultExpense.paymentDate = new Date(resultExpense.paymentDate);
      }
      normalizeExpenseNumbers(resultExpense);

      // Update local state
      setExpenses((prevExpenses) =>
        prevExpenses.map((exp) =>
          exp.id === resultExpense.id ? resultExpense : exp
        )
      );
      // Record for undo. Restore the pre-paid version of the expense.
      setUndoStack((prev) => [
        ...prev,
        { type: "paid", previousExpense: expense },
      ]);
      setUndoSnackbar({
        open: true,
        message: `Marked "${expense.name}" as paid`,
        canUndo: true,
      });
      return true;
    } catch (error) {
      captureErrorWithContext(error as Error, {
        component: "ExpensesPage",
        action: "togglePaid",
        expenseId: expense.id,
      });
      alert("Failed to mark expense as paid. Please try again.");
      return false;
    }
  };

  const handleExpenseSync = (syncedExpense: Expense) => {
    if (!syncedExpense?.id) return;
    const next = { ...syncedExpense };
    if (typeof next.dueDate === "string") {
      next.dueDate = new Date(next.dueDate);
    }
    if (typeof next.invoicedDate === "string") {
      next.invoicedDate = new Date(next.invoicedDate);
    }
    if (typeof next.paymentDate === "string") {
      next.paymentDate = new Date(next.paymentDate);
    }
    normalizeExpenseNumbers(next);
    console.log("[ExpensesPage] Synced shopping list expense into state", {
      expenseId: next.id,
      amount: next.amount,
      linkedShoppingListId: next.linkedShoppingListId,
    });
    setExpenses((prevExpenses) =>
      prevExpenses.map((expense) => (expense.id === next.id ? next : expense))
    );
  };

  const parseCsv = (text: string): string[][] => {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i += 1) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (inQuotes) {
        if (char === '"' && nextChar === '"') {
          field += '"';
          i += 1;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          field += char;
        }
      } else if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        row.push(field);
        field = "";
      } else if (char === "\n") {
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
      } else if (char !== "\r") {
        field += char;
      }
    }

    if (field.length || row.length) {
      row.push(field);
      rows.push(row);
    }

    return rows.filter((r) => r.some((value) => value.trim() !== ""));
  };

  const parseAmount = (value: string): number => {
    if (!value) return 0;
    const cleaned = value.replace(/[$,]/g, "").replace(/[()]/g, "").trim();
    const parsed = Number.parseFloat(cleaned);
    if (Number.isNaN(parsed)) return 0;
    return Math.abs(parsed);
  };

  const parseDueDate = (value: string): Date => {
    const trimmed = value.trim();
    const dayMatch = trimmed.match(/^(\d{1,2})(st|nd|rd|th)?$/i);
    if (dayMatch) {
      const day = Number.parseInt(dayMatch[1], 10);
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), day);
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  };

  const handleImportFile = async (file: File) => {
    try {
      setIsImporting(true);
      const text = await file.text();
      console.log("[IMPORT DEBUG] CSV text:", text.substring(0, 500));
      const rows = parseCsv(text);
      console.log("[IMPORT DEBUG] Parsed rows count:", rows.length);
      const header = rows[0]?.map((value) => value.trim()) || [];
      console.log("[IMPORT DEBUG] Header:", header);
      const {
        itemIdx,
        statusIdx,
        dueDateIdx,
        invoicedDateIdx,
        amountIdx,
        categoryIdx,
        businessIdx,
        notesIdx,
        missingColumns,
      } = getImportColumnIndices(header);

      console.log("[IMPORT DEBUG] Column indices:", {
        itemIdx,
        statusIdx,
        dueDateIdx,
        invoicedDateIdx,
        amountIdx,
        categoryIdx,
        businessIdx,
        notesIdx,
        missingColumns,
      });

      if (missingColumns.length > 0) {
        throw new Error(
          `Missing required CSV columns: ${missingColumns.join(", ")}`
        );
      }

      const expensesToImport = rows
        .slice(1)
        .map((row) => {
          const name = (row[itemIdx] || "").trim();
          if (!name || /^[\d,.-]+$/.test(name)) {
            return null;
          }

          const status = (row[statusIdx] || "").toLowerCase();
          const isPaid = status.includes("paid");
          const isRecurring = status.includes("active");
          const dueDateValue = row[dueDateIdx] || "";
          const invoicedDateValue =
            invoicedDateIdx >= 0 ? row[invoicedDateIdx] || "" : "";
          const amountValue = row[amountIdx] || "";
          const amount = parseAmount(amountValue);

          if (!amount) {
            return null;
          }

          return {
            name,
            amount,
            invoicedDate: invoicedDateValue
              ? parseDueDate(invoicedDateValue)
              : undefined,
            dueDate: parseDueDate(dueDateValue),
            isPaid,
            isRecurring,
            recurrencePattern: isRecurring ? "monthly" : undefined,
            vendor: (row[businessIdx] || "").trim(),
            internalCategory: (row[categoryIdx] || "uncategorized").trim(),
            notes: (row[notesIdx] || "").trim() || undefined,
          };
        })
        .filter((expense) => expense !== null);

      console.log("[IMPORT DEBUG] Expenses to import:", expensesToImport);
      console.log("[IMPORT DEBUG] Count to import:", expensesToImport.length);

      const imported = await expenseAPI.importMany(
        expensesToImport as Omit<Expense, "id">[]
      );
      console.log("[IMPORT DEBUG] API response (imported):", imported);

      const formatted = imported.map((expense) => {
        const formattedExpense = { ...expense };
        if (typeof expense.dueDate === "string") {
          formattedExpense.dueDate = new Date(expense.dueDate);
        }
        if (typeof expense.invoicedDate === "string") {
          formattedExpense.invoicedDate = new Date(expense.invoicedDate);
        }
        if (typeof expense.amount === "string") {
          formattedExpense.amount = parseFloat(expense.amount);
        }
        return formattedExpense;
      });
      console.log("[IMPORT DEBUG] Formatted expenses:", formatted);
      console.log(
        "[IMPORT DEBUG] Current expenses count before update:",
        expenses.length
      );
      setExpenses((prev) => {
        console.log("[IMPORT DEBUG] Previous expenses:", prev.length);
        const newExpenses = [...prev, ...formatted];
        console.log("[IMPORT DEBUG] New expenses total:", newExpenses.length);
        return newExpenses;
      });
    } catch (error) {
      console.error("[IMPORT DEBUG] Error:", error);
      captureErrorWithContext(error as Error, {
        component: "ExpensesPage",
        action: "importExpensesCsv",
      });
      alert("Expense import failed. Please check the CSV format.");
    } finally {
      setIsImporting(false);
    }
  };

  const onImportFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";
    await handleImportFile(file);
  };

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const runTransactionAnalysis = async () => {
    try {
      setIsAnalyzing(true);
      logger.info("Starting transaction analysis", { daysBack: 60 });

      const result = await transactionService.analyzeTransactions(60);

      logger.info("Transaction analysis completed", { ...result });

      if (result.linkedCount > 0) {
        // Reload expenses to reflect the updated paid status
        const updatedExpenses = await expenseAPI.getAll();
        const formattedExpenses = updatedExpenses.map((expense) => {
          const formatted = { ...expense };
          if (typeof expense.dueDate === "string") {
            formatted.dueDate = new Date(expense.dueDate);
          }
          if (typeof expense.invoicedDate === "string") {
            formatted.invoicedDate = new Date(expense.invoicedDate);
          }
          if (typeof expense.amount === "string") {
            formatted.amount = parseFloat(expense.amount);
          }
          return formatted;
        });
        setExpenses(formattedExpenses);
      }

      setLastAnalysis(`Linked ${result.linkedCount} transactions to expenses`);
    } catch (error) {
      captureErrorWithContext(error as Error, {
        component: "ExpensesPage",
        action: "runTransactionAnalysis",
      });
      setLastAnalysis("Analysis failed - check console for details");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const visibleExpenses = useMemo(() => {
    if (!sharedOnly) return expenses;
    return expenses.filter(
      (expense) =>
        (expense as Expense & { is_shared?: boolean }).is_shared ||
        expense.isShared
    );
  }, [expenses, sharedOnly]);

  const profileScopedExpenses = useMemo(() => {
    if (!expenseProfileId) return visibleExpenses;
    return visibleExpenses.filter((expense) => {
      const isSharedExpense =
        (expense as Expense & { is_shared?: boolean }).is_shared ||
        expense.isShared;
      if (isSharedExpense) {
        return true;
      }
      const scopedTo = expense.expensesProfileId;
      return !scopedTo || scopedTo === expenseProfileId;
    });
  }, [expenseProfileId, visibleExpenses]);

  const normalizedSearch = useMemo(
    () => appliedSearchQuery.trim().toLowerCase(),
    [appliedSearchQuery]
  );

  const filteredExpenses = useMemo(() => {
    return profileScopedExpenses.filter((expense) => {
      const matchesSearch = !normalizedSearch
        ? true
        : [expense.name, expense.vendor]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(normalizedSearch));
      const matchesPaid =
        paidFilter === "all"
          ? true
          : paidFilter === "paid"
            ? expense.isPaid
            : !expense.isPaid;
      const matchesRecurring =
        recurringFilter === "all"
          ? true
          : recurringFilter === "recurring"
            ? expense.isRecurring
            : !expense.isRecurring;
      const matchesCategory =
        categoryFilter === "all"
          ? true
          : expense.internalCategory === categoryFilter;
      const matchesVendor =
        vendorFilter === "all" ? true : expense.vendor === vendorFilter;
      // Postponed expenses drop out of the list until their date passes. A
      // debt-linked expense IS the debt, so postponing the debt hides it too.
      const linkedDebt = expense.linkedDebtId
        ? debts.find((d) => d.id === expense.linkedDebtId)
        : undefined;
      const notPostponed = !(
        isPostponedNow(expense.isPostponed, expense.postponedUntil) ||
        (linkedDebt && isDebtPostponed(linkedDebt))
      );

      return (
        matchesSearch &&
        matchesPaid &&
        matchesRecurring &&
        matchesCategory &&
        matchesVendor &&
        notPostponed
      );
    });
  }, [
    profileScopedExpenses,
    normalizedSearch,
    paidFilter,
    recurringFilter,
    categoryFilter,
    vendorFilter,
    debts,
  ]);

  const workingDaysMetrics = useMemo(() => {
    const today = getTodayLocal();

    const getEndForWorkingDays = (count: number) => {
      // Find the end date (inclusive) for the next N working days (Mon-Fri).
      let workingDays = 0;
      let cursor = new Date(today);
      while (workingDays < count) {
        const day = cursor.getUTCDay(); // 0=Sun, 6=Sat
        if (day !== 0 && day !== 6) {
          workingDays += 1;
          if (workingDays === count) break;
        }
        cursor = addDays(cursor, 1);
      }
      return cursor;
    };

    const end20 = getEndForWorkingDays(20);
    const end5 = getEndForWorkingDays(5);

    const toUtcDateOnly = (date: Date) => {
      return new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
      );
    };
    const todayKey = toUtcDateOnly(today).getTime();
    const end20Key = toUtcDateOnly(end20).getTime();
    const end5Key = toUtcDateOnly(end5).getTime();

    const list = filteredExpenses
      .filter((expense) => !expense.isPaid && !expense.isSkipped)
      .filter((expense) => expense.dueDate);

    const sumToEndKey = (endKey: number) => {
      return list
        .filter((expense) => {
          const dueKey = toUtcDateOnly(expense.dueDate).getTime();
          return dueKey >= todayKey && dueKey <= endKey;
        })
        .reduce((sum, expense) => {
          return (
            sum +
            expenseUtils.getEffectiveExpenseAmount(expense, debts, {
              income,
              taxProfiles,
            })
          );
        }, 0);
    };

    const total20 = sumToEndKey(end20Key);
    const total5 = sumToEndKey(end5Key);

    return {
      next20WorkingDaysAverage: total20 / 20,
      next5WorkingDaysTotal: total5,
    };
  }, [filteredExpenses, debts, income, taxProfiles]);

  const categorizedExpenses = useMemo(
    () => expenseUtils.categorizeExpenses(filteredExpenses),
    [filteredExpenses]
  );
  const pinnedAdjustedExpenses = useMemo(() => {
    if (pinnedExpenseIds.size === 0) return categorizedExpenses;
    const sectionPriority: ExpenseSection[] = [
      "overdue",
      "today",
      "tomorrow",
      "thisWeek",
      "next10Days",
      "thisMonth",
      "nextMonth",
      "expected",
      "upcoming",
      "received",
    ];
    const pinnedSectionMap = new Map<string, ExpenseSection>();
    sectionPriority.forEach((section) => {
      (categorizedExpenses[section] || []).forEach((expense) => {
        if (!pinnedSectionMap.has(expense.id)) {
          pinnedSectionMap.set(expense.id, section);
        }
      });
    });
    const pinnedExpenseBase = filteredExpenses
      // Never pin paid items back into "Today" (or any other due bucket).
      .filter((expense) => pinnedExpenseIds.has(expense.id) && !expense.isPaid)
      .map((expense) => ({
        ...expense,
        pinnedFromSection: pinnedSectionMap.get(expense.id),
      }));
    const expectedIds = new Set(
      (categorizedExpenses.expected || []).map((expected) => expected.id)
    );
    const pinnedExpectedExpenses = pinnedExpenseBase.filter((expense) =>
      expectedIds.has(expense.id)
    );
    const pinnedTodayExpected = pinnedExpectedExpenses.map((expense) => ({
      ...expense,
      pinnedExpectedGroupKey: "today",
    }));
    const pinnedExpenses = pinnedExpenseBase;
    const pinnedBaseIds = new Set(
      pinnedExpenseBase.map((expense) => expense.id)
    );
    const result = {} as Record<ExpenseSection, Expense[]>;
    (Object.keys(categorizedExpenses) as ExpenseSection[]).forEach(
      (section) => {
        let list = categorizedExpenses[section] || [];
        if (section === "expected") {
          list = list.filter((expense) => !pinnedBaseIds.has(expense.id));
          list = [...list, ...pinnedTodayExpected];
        } else {
          list = list.filter((expense) => !pinnedBaseIds.has(expense.id));
        }
        if (section === "today") {
          list = [...list, ...pinnedExpenses];
        }
        result[section] = list;
      }
    );
    return result;
  }, [categorizedExpenses, filteredExpenses, pinnedExpenseIds]);
  const baseSectionTotals = useMemo(() => {
    return expenseUtils.calculateSectionTotals(pinnedAdjustedExpenses, debts, {
      income,
      taxProfiles,
    });
  }, [pinnedAdjustedExpenses, debts, income, taxProfiles]);

  const isSameUtcDay = (a: Date, b: Date) =>
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate();

  const toUtcDateOnly = (date: Date) =>
    new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    );

  const getExpectedGroupKey = useCallback((expense: Expense) => {
    if (!expense?.dueDate) return "remainder";
    const todayUTC = getTodayLocal();
    const tomorrowUTC = addDays(todayUTC, 1);
    const next7EndUTC = addDays(todayUTC, 7);
    const next10EndUTC = addDays(todayUTC, 10);
    const dueUTC = toUtcDateOnly(expense.dueDate);
    if (dueUTC.getTime() < todayUTC.getTime()) return "overdue";
    if (isSameUtcDay(dueUTC, todayUTC)) return "today";
    if (isSameUtcDay(dueUTC, tomorrowUTC)) return "tomorrow";
    if (dueUTC.getTime() <= next7EndUTC.getTime()) return "next7";
    if (dueUTC.getTime() <= next10EndUTC.getTime()) return "next10";
    return "remainder";
  }, []);

  const nextMonthSummaryTotal = useMemo(() => {
    const nextMonthTarget = addMonths(new Date(), 1);
    const sectionExpenses = pinnedAdjustedExpenses.nextMonth || [];
    const expectedRemainderNextMonth = (
      pinnedAdjustedExpenses.expected || []
    ).filter((expense) => {
      if (!expense.dueDate) return false;
      if (getExpectedGroupKey(expense) !== "remainder") return false;
      return (
        expense.dueDate.getUTCFullYear() === nextMonthTarget.getUTCFullYear() &&
        expense.dueDate.getUTCMonth() === nextMonthTarget.getUTCMonth()
      );
    });

    const getBaseId = (expense: Expense & { originalId?: string }) =>
      (expense as Expense & { originalId?: string }).originalId || expense.id;
    const baseIds = new Set(sectionExpenses.map(getBaseId));
    const missingFromNextMonth = expectedRemainderNextMonth.filter(
      (expense) => {
        const baseId = getBaseId(expense as Expense & { originalId?: string });
        return !baseIds.has(baseId);
      }
    );
    const displayExpenses = [...sectionExpenses, ...missingFromNextMonth];
    const total = displayExpenses
      .filter((expense) => !expense.isSkipped)
      .reduce(
        (sum, expense) =>
          sum +
          expenseUtils.getEffectiveExpenseAmount(expense, debts, {
            income,
            taxProfiles,
          }),
        0
      );

    return total;
  }, [pinnedAdjustedExpenses, debts, income, taxProfiles, getExpectedGroupKey]);

  // The Overdue card must match the Overdue rows shown in the table, which
  // only list overdue items due within the last 30 days (mirroring the
  // categorizeExpenses 30-day window that also feeds "expected"). The raw
  // `overdue` bucket additionally contains every older recurring back-instance
  // (e.g. a monthly bill missed for 6 months), so summing it inflated the card
  // far above the table total. Constrain the card's overdue total to the same
  // 30-day window.
  const overdueSummaryTotal = useMemo(() => {
    const todayUTC = getTodayLocal();
    const windowStartTime = toUtcDateOnly(addDays(todayUTC, -30)).getTime();
    return (pinnedAdjustedExpenses.overdue || [])
      .filter((expense) => !expense.isSkipped)
      .filter((expense) => {
        if (!expense.dueDate) return false;
        return toUtcDateOnly(expense.dueDate).getTime() >= windowStartTime;
      })
      .reduce(
        (sum, expense) =>
          sum +
          expenseUtils.getEffectiveExpenseAmount(expense, debts, {
            income,
            taxProfiles,
          }),
        0
      );
  }, [pinnedAdjustedExpenses, debts, income, taxProfiles]);

  const sectionTotals = useMemo(
    () =>
      baseSectionTotals.map((section) => {
        if (section.section === "nextMonth") {
          return { ...section, total: nextMonthSummaryTotal };
        }
        if (section.section === "overdue") {
          return { ...section, total: overdueSummaryTotal };
        }
        return section;
      }),
    [baseSectionTotals, nextMonthSummaryTotal, overdueSummaryTotal]
  );

  // Quick lookup of each section's expense total (for cumulative roll-ups).
  const expenseTotalsBySection = useMemo(() => {
    const map: Partial<Record<ExpenseSection, number>> = {};
    sectionTotals.forEach((s) => {
      map[s.section] = s.total;
    });
    return map;
  }, [sectionTotals]);

  // Categorize income for net calculation
  const categorizedIncome = useMemo(
    () => incomeUtils.categorizeIncome(income),
    [income]
  );
  const incomeSectionTotals = useMemo(
    () => incomeUtils.calculateIncomeSectionTotals(categorizedIncome),
    [categorizedIncome]
  );

  const incomeTotalsBySection = useMemo(() => {
    const map: Record<string, number> = {};
    incomeSectionTotals.forEach((s) => {
      map[s.section] = s.total;
    });
    return map;
  }, [incomeSectionTotals]);

  const pendingDepositIncomeItems = useMemo(
    () =>
      (categorizedIncome.pendingDeposit || []).filter(
        (item) => !item.isDepositGhost
      ),
    [categorizedIncome]
  );

  const pendingDepositIncomeTotal = useMemo(
    () =>
      pendingDepositIncomeItems.reduce(
        (sum, item) => sum + incomeUtils.getNetDepositedTotal(item),
        0
      ),
    [pendingDepositIncomeItems]
  );

  const availableBalanceAccounts = useMemo(
    () =>
      bankAccounts.map((account) => ({
        ...account,
        resolvedAvailable:
          account.available !== null ? account.available : account.balance,
      })),
    [bankAccounts]
  );

  const availableBalanceTotal = useMemo(
    () =>
      availableBalanceAccounts.reduce(
        (sum, account) => sum + (account.resolvedAvailable || 0),
        0
      ),
    [availableBalanceAccounts]
  );

  const overdueIncomeBaseTotal = incomeTotalsBySection.overdue || 0;
  const overdueIncomeAugmentedTotal =
    overdueIncomeBaseTotal +
    (includePendingDepositsInOverdue ? pendingDepositIncomeTotal : 0) +
    (includeAvailableBalancesInOverdue ? availableBalanceTotal : 0);

  const handleIncomeSummaryClick = (sectionKey: ExpenseSection) => {
    if (sectionKey === "overdue") {
      setOverdueIncomeBreakdownOpen(true);
      return;
    }
    const sectionIncome = categorizedIncome[sectionKey] || [];
    if (sectionIncome.length === 0) {
      console.log("[ExpensesPage] No income items for section:", sectionKey);
      return;
    }
    console.log("[ExpensesPage] Opening income modal from summary:", {
      section: sectionKey,
      count: sectionIncome.length,
    });
    setIncomeModalSection(sectionKey);
    setIncomeModalOpen(true);
  };

  const togglePinExpense = (expense: Expense) => {
    setPinnedExpenseIds((prev) => {
      const next = new Set(prev);
      if (next.has(expense.id)) {
        next.delete(expense.id);
        console.log("[ExpensesPage] Unpinned expense", {
          expenseId: expense.id,
        });
      } else {
        next.add(expense.id);
        console.log("[ExpensesPage] Pinned expense", { expenseId: expense.id });
      }
      return next;
    });
  };

  const formatExpectedDate = (date?: Date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", { timeZone: "UTC" });
  };

  const formatExpectedInputDate = (date?: Date) => {
    if (!date) return "";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  const startEditExpectedDate = (inc: Income) => {
    setEditingIncomeId(inc.id);
    setEditingExpectedDate(formatExpectedInputDate(inc.expectedByDate));
    console.debug("[ExpensesPage] Edit income expected date", {
      incomeId: inc.id,
      expectedByDate: inc.expectedByDate,
    });
  };

  const cancelEditExpectedDate = () => {
    console.debug("[ExpensesPage] Cancel income expected date edit", {
      incomeId: editingIncomeId,
    });
    setEditingIncomeId(null);
    setEditingExpectedDate("");
  };

  const saveEditExpectedDate = async (inc: Income) => {
    if (!editingExpectedDate) {
      cancelEditExpectedDate();
      return;
    }
    try {
      setEditingExpectedSaving(true);
      const updatedIncome: Income = {
        ...inc,
        expectedByDate: new Date(`${editingExpectedDate}T00:00:00Z`),
      };
      console.debug("[ExpensesPage] Save income expected date", {
        incomeId: inc.id,
        expectedByDate: updatedIncome.expectedByDate,
      });
      await handleUpdateIncome(updatedIncome);
      setEditingIncomeId(null);
      setEditingExpectedDate("");
    } catch (error) {
      console.error("[ExpensesPage] Failed to save expected date", error);
    } finally {
      setEditingExpectedSaving(false);
    }
  };

  const incomeModalItems = useMemo(() => {
    if (!incomeModalSection) return [];
    return categorizedIncome[incomeModalSection] || [];
  }, [categorizedIncome, incomeModalSection]);

  useEffect(() => {
    if (!incomeModalOpen || !incomeModalSection) return;
    console.debug("[ExpensesPage] Income modal expected dates", {
      section: incomeModalSection,
      count: incomeModalItems.length,
      sample: incomeModalItems.slice(0, 12).map((inc) => ({
        id: inc.id,
        expectedByDate:
          inc.expectedByDate instanceof Date
            ? inc.expectedByDate.toISOString()
            : inc.expectedByDate,
        formatted: formatExpectedDate(inc.expectedByDate),
      })),
    });
  }, [incomeModalOpen, incomeModalSection, incomeModalItems]);

  const getSectionColor = (section: string) => {
    // Greyscale colors from light to dark
    switch (section) {
      case "overdue":
        return "#374151"; // Darkest grey
      case "today":
        return "#4B5563";
      case "tomorrow":
        return "#6B7280";
      case "thisWeek":
        return "#9CA3AF";
      case "thisMonth":
        return "#D1D5DB";
      case "nextMonth":
        return "#E5E7EB";
      case "expected":
        return "#F3F4F6"; // Lightest grey
      default:
        return "#9CA3AF";
    }
  };

  const getSectionLabel = (section: string) => {
    switch (section) {
      case "overdue":
        return "Overdue";
      case "today":
        return "Today";
      case "tomorrow":
        return "Tomorrow";
      case "thisWeek":
        return "Within 7 Days";
      case "next10Days":
        return "Within 10 Days";
      case "thisMonth":
        return expenseUtils.getCurrentMonthName();
      case "nextMonth":
        return expenseUtils.getNextMonthName();
      case "expected":
        return "Within 30 Days";
      default:
        return section;
    }
  };

  const billBudgetSections = useMemo(
    () => new Set<ExpenseSection>(BILL_BUDGET_SECTIONS),
    []
  );

  const billBudgetBreakdownBySection = useMemo(() => {
    const result: Partial<
      Record<ExpenseSection, { bills: number; budgets: number; total: number }>
    > = {};
    Array.from(billBudgetSections).forEach((sectionKey) => {
      const sectionExpenses = (pinnedAdjustedExpenses[sectionKey] || []).filter(
        (expense) => !expense.isSkipped
      );
      const bills = sectionExpenses.filter(
        (expense) => (expense.billOrBudget || "bill") === "bill"
      ).length;
      const budgets = sectionExpenses.filter(
        (expense) => (expense.billOrBudget || "bill") === "budget"
      ).length;
      const total = bills + budgets;
      result[sectionKey] = { bills, budgets, total };
    });
    return result;
  }, [billBudgetSections, pinnedAdjustedExpenses]);

  const renderBillBudgetBar = (sectionKey: ExpenseSection) => {
    if (!billBudgetSections.has(sectionKey)) return null;
    const breakdown = billBudgetBreakdownBySection[sectionKey] || {
      bills: 0,
      budgets: 0,
      total: 0,
    };
    const { bills, budgets, total } = breakdown;
    const billPercent = total > 0 ? (bills / total) * 100 : 0;
    const budgetPercent = total > 0 ? (budgets / total) * 100 : 0;
    return (
      <Tooltip
        title={`${bills} Bill${bills === 1 ? "" : "s"} • ${budgets} Budget${
          budgets === 1 ? "" : "s"
        }`}
        arrow
      >
        <Box
          sx={{
            width: "100%",
            height: 6,
            borderRadius: 999,
            backgroundColor: isDarkMode ? "#2d2d2d" : "#e2e8f0",
            overflow: "hidden",
            display: "flex",
          }}
        >
          <Box
            sx={{
              width: `${billPercent}%`,
              backgroundColor: "#4B5563",
            }}
          />
          <Box
            sx={{
              width: `${budgetPercent}%`,
              backgroundColor: "#38A169",
            }}
          />
        </Box>
      </Tooltip>
    );
  };

  const loadingDebugDetails = useMemo(
    () => ({
      expenses: expenses.length,
      income: income.length,
      debts: debts.length,
      viewMode,
      sharedOnly,
      appliedSearchQuery,
      paidFilter,
      recurringFilter,
      categoryFilter,
      vendorFilter,
      analyzing: isAnalyzing,
      importing: isImporting,
      demoMode: demoService.isDemo(),
      cacheHydrated: loadPerf.cacheHydrated,
      cacheAgeMs: loadPerf.cacheAgeMs,
      cacheParseMs: loadPerf.cacheParseMs,
      expensesFetchMs: loadPerf.expensesFetchMs,
      incomeFetchMs: loadPerf.incomeFetchMs,
      debtsFetchMs: loadPerf.debtsFetchMs,
      networkTotalMs: loadPerf.networkTotalMs,
    }),
    [
      expenses.length,
      income.length,
      debts.length,
      viewMode,
      sharedOnly,
      appliedSearchQuery,
      paidFilter,
      recurringFilter,
      categoryFilter,
      vendorFilter,
      isAnalyzing,
      isImporting,
      loadPerf.cacheHydrated,
      loadPerf.cacheAgeMs,
      loadPerf.cacheParseMs,
      loadPerf.expensesFetchMs,
      loadPerf.incomeFetchMs,
      loadPerf.debtsFetchMs,
      loadPerf.networkTotalMs,
    ]
  );

  // Which top-level tab of the Expenses page is showing.
  const recurringTab = searchParams.get("view") === "recurring";
  const setExpensesTab = (view: "expenses" | "recurring") => {
    const next = new URLSearchParams(searchParams);
    if (view === "recurring") next.set("view", "recurring");
    else next.delete("view");
    next.delete("recurring");
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      {/* Hidden file input for CSV import */}
      <input
        ref={importInputRef}
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        onChange={onImportFileChange}
      />

      {/* Main Content */}
      <Box sx={{ maxWidth: 1200, margin: "0 auto", padding: { xs: 2, md: 3 } }}>
        {isRefreshing && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              backgroundColor: isDarkMode
                ? "rgba(255,255,255,0.06)"
                : "rgba(0,0,0,0.04)",
            }}
          >
            Refreshing latest data…
          </Alert>
        )}

        {/* Expenses vs. Recurring Expenses tabs */}
        <Tabs
          value={recurringTab ? 1 : 0}
          onChange={(_e, v) =>
            setExpensesTab(v === 1 ? "recurring" : "expenses")
          }
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Expenses" sx={{ textTransform: "none" }} />
          <Tab label="Recurring Expenses" sx={{ textTransform: "none" }} />
        </Tabs>

        {recurringTab && (
          <RecurringBillsDialog
            variant="page"
            open
            onClose={() => setExpensesTab("expenses")}
            expenses={expenses}
            debts={debts}
            context={{ income, taxProfiles }}
            onAddBill={() => {
              setIsEditing(false);
              setCurrentExpense(undefined);
              setIsOpen(true);
            }}
            onEditBill={(id) => {
              const target = expenses.find((e) => e.id === id);
              if (target) handleEditExpense(target);
            }}
            onDeleteBill={(id) => {
              const target = expenses.find((e) => e.id === id);
              if (target) handleDeleteExpense(target);
            }}
            onEditDebt={(debtId) => {
              const target = debts.find((d) => d.id === debtId);
              if (target) {
                setCurrentDebt(target);
                setDebtDrawerOpen(true);
              }
            }}
            onDeleteDebt={(debtId) => handleDeleteDebtById(debtId)}
            onPostponeDebt={handlePostponeDebt}
            onPostponeExpense={handlePostponeExpense}
            onSetExpensePriority={handleSetExpensePriority}
            onSetExpenseRank={handleSetExpenseRank}
          />
        )}

        <Box sx={{ display: recurringTab ? "none" : "block" }}>
          {/* Summary Section - Always visible */}
          {(() => {
            const summarySections: ExpenseSection[] = [
              "overdue",
              "today",
              "tomorrow",
              "thisWeek",
              "next10Days",
              "thisMonth",
              "nextMonth",
            ];
            return (
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Box />
                  <Tooltip
                    title="When on, Overdue is folded into any card showing a cumulative total"
                    arrow
                    placement="left"
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={includeOverdueInCumulative}
                          onChange={toggleIncludeOverdueInCumulative}
                        />
                      }
                      label="Include Overdue in cumulative totals"
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.8rem",
                          color: isDarkMode
                            ? "rgba(255,255,255,0.7)"
                            : "text.secondary",
                        },
                      }}
                    />
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(3, minmax(0, 1fr))",
                      md: `repeat(${summarySections.length}, minmax(0, 1fr))`,
                    },
                    gap: 2,
                    marginBottom: 2,
                  }}
                >
                  {sectionTotals
                    .filter((section) =>
                      summarySections.includes(
                        section.section as ExpenseSection
                      )
                    )
                    .map((section) => {
                      const isOverdueSection = section.section === "overdue";
                      // Cumulative roll-up (per-card toggle). Overdue and the
                      // first chain bucket (Today) have nothing earlier to add,
                      // so they don't expose the toggle.
                      const canCumulate = canSectionCumulate(
                        section.section as ExpenseSection
                      );
                      const isCumulative =
                        canCumulate &&
                        cumulativeSections.has(
                          section.section as ExpenseSection
                        );
                      const includedSections = isCumulative
                        ? getCumulativeIncludedSections(
                            section.section as ExpenseSection
                          )
                        : [section.section as ExpenseSection];

                      const displayedExpenseTotal = isCumulative
                        ? includedSections.reduce(
                            (sum, s) => sum + (expenseTotalsBySection[s] || 0),
                            0
                          )
                        : section.total;

                      // For Expenses page, show Income - Expenses
                      const ownIncomeTotal = isOverdueSection
                        ? overdueIncomeAugmentedTotal
                        : incomeTotalsBySection[section.section] || 0;
                      const incomeTotal = isCumulative
                        ? includedSections.reduce(
                            (sum, s) => sum + (incomeTotalsBySection[s] || 0),
                            0
                          )
                        : ownIncomeTotal;
                      const netValue = incomeTotal - displayedExpenseTotal;
                      const isPositive = netValue >= 0;

                      // Tooltip text listing the earlier buckets rolled up.
                      const earlierLabels = includedSections
                        .filter((s) => s !== section.section)
                        .map((s) => getSectionLabel(s));
                      const cumulativeTooltip = isCumulative
                        ? earlierLabels.length > 0
                          ? `Cumulative — includes ${earlierLabels.join(", ")}`
                          : "Cumulative"
                        : "Show cumulative total (this + earlier buckets)";

                      const isSelected = expandedSection === section.section;

                      return (
                        <Box key={section.section}>
                          <Card
                            onClick={() => {
                              if (expandedSection === section.section) {
                                handleSectionChange(null);
                              } else {
                                handleSectionChange(
                                  section.section as ExpenseSection
                                );
                              }
                            }}
                            sx={{
                              borderTop: `4px solid ${getSectionColor(
                                section.section
                              )}`,
                              height: "100%",
                              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
                              boxShadow: isSelected
                                ? `0 0 0 2px ${getSectionColor(section.section)}`
                                : isDarkMode
                                  ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                                  : "0 1px 2px rgba(0,0,0,0.05)",
                              cursor: "pointer",
                              transition:
                                "transform 0.1s ease-in-out, box-shadow 0.1s ease-in-out",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: isSelected
                                  ? `0 4px 6px rgba(0,0,0,0.1), 0 0 0 2px ${getSectionColor(
                                      section.section
                                    )}`
                                  : "0 4px 6px rgba(0,0,0,0.1)",
                              },
                            }}
                          >
                            <CardContent
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                position: "relative",
                              }}
                            >
                              {canCumulate && !isLoading && (
                                <Tooltip
                                  title={cumulativeTooltip}
                                  arrow
                                  placement="top"
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleCumulativeSection(
                                        section.section as ExpenseSection
                                      );
                                    }}
                                    sx={{
                                      position: "absolute",
                                      top: 4,
                                      right: 4,
                                      width: 24,
                                      height: 24,
                                      color: isCumulative
                                        ? "#fff"
                                        : isDarkMode
                                          ? "#a0aec0"
                                          : "#718096",
                                      backgroundColor: isCumulative
                                        ? getSectionColor(section.section)
                                        : "transparent",
                                      border: `1px solid ${
                                        isCumulative
                                          ? getSectionColor(section.section)
                                          : isDarkMode
                                            ? "#4a5568"
                                            : "#cbd5e0"
                                      }`,
                                      "&:hover": {
                                        backgroundColor: isCumulative
                                          ? getSectionColor(section.section)
                                          : isDarkMode
                                            ? "rgba(255,255,255,0.08)"
                                            : "rgba(0,0,0,0.04)",
                                      },
                                    }}
                                  >
                                    <FunctionsIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                                sx={{ pr: canCumulate ? 3 : 0 }}
                              >
                                {getSectionLabel(section.section)}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: "bold",
                                  color:
                                    displayedExpenseTotal > 0
                                      ? "#E53E3E"
                                      : undefined,
                                }}
                              >
                                {isLoading ? (
                                  <Skeleton
                                    variant="text"
                                    width={96}
                                    sx={{ display: "inline-block" }}
                                  />
                                ) : displayedExpenseTotal > 0 ? (
                                  `-${expenseUtils.formatCurrency(
                                    displayedExpenseTotal
                                  )}`
                                ) : (
                                  expenseUtils.formatCurrency(
                                    displayedExpenseTotal
                                  )
                                )}
                              </Typography>
                              {isLoading ? (
                                <Skeleton
                                  variant="text"
                                  width={72}
                                  sx={{ display: "inline-block", mt: 0.5 }}
                                />
                              ) : incomeTotal > 0 ? (
                                <Tooltip
                                  title={
                                    isOverdueSection
                                      ? "Click to view overdue income + balance breakdown"
                                      : "Click to view income details"
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Typography
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleIncomeSummaryClick(
                                        section.section as ExpenseSection
                                      );
                                    }}
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      color: isPositive ? "#38A169" : "#E53E3E",
                                      marginTop: "4px",
                                      cursor: "pointer",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                  >
                                    {isPositive ? "+" : ""}
                                    {expenseUtils.formatCurrency(netValue)}
                                  </Typography>
                                </Tooltip>
                              ) : null}
                              <Box sx={{ mt: "auto", pt: 1 }}>
                                {isLoading ? (
                                  <Skeleton variant="rounded" height={6} />
                                ) : (
                                  renderBillBudgetBar(
                                    section.section as ExpenseSection
                                  )
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>
                      );
                    })}
                </Box>

                <Box
                  sx={{
                    display: "inline-block",
                    width: "100%",
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    backgroundColor: isDarkMode
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                    color: isDarkMode ? "#E5E7EB" : "#374151",
                    fontSize: "12px",
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  Daily Average for the within 20 working days is{" "}
                  {isLoading ? (
                    <Skeleton
                      variant="text"
                      width={72}
                      sx={{ display: "inline-block" }}
                    />
                  ) : (
                    expenseUtils.formatCurrency(
                      workingDaysMetrics.next20WorkingDaysAverage
                    )
                  )}
                  {" • "}
                  Within 5 working days total is{" "}
                  {isLoading ? (
                    <Skeleton
                      variant="text"
                      width={72}
                      sx={{ display: "inline-block" }}
                    />
                  ) : (
                    expenseUtils.formatCurrency(
                      workingDaysMetrics.next5WorkingDaysTotal
                    )
                  )}
                </Box>
              </Box>
            );
          })()}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 0.5,
              mb: 1,
            }}
          >
            <Tooltip title="Profiles" arrow>
              <IconButton
                aria-label="Profiles"
                onClick={(e) => setProfileMenuAnchorEl(e.currentTarget)}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  width: 40,
                  height: 40,
                  alignSelf: "center",
                  my: 0.25,
                }}
              >
                <ManageAccountsIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Expense Lists or Empty State */}
              <ExpensesSearchControls
                appliedSearchQuery={appliedSearchQuery}
                onApplySearch={handleSearchApply}
                paidFilter={paidFilter}
                setPaidFilter={setPaidFilter}
                recurringFilter={recurringFilter}
                setRecurringFilter={setRecurringFilter}
                categoryFilter={categoryFilter}
                setCategoryFilter={setCategoryFilter}
                vendorFilter={vendorFilter}
                setVendorFilter={setVendorFilter}
                expenses={expenses}
                sharedOnly={sharedOnly}
                toggleSharedOnly={() => setSharedOnly((prev) => !prev)}
                allExpanded={allExpanded}
                toggleAllExpanded={() => setAllExpanded((prev) => !prev)}
                groupingMode={groupingMode}
                onGroupingModeChange={handleGroupingModeChange}
              />
            </Box>
          </Box>

          <Popover
            open={Boolean(profileMenuAnchorEl)}
            anchorEl={profileMenuAnchorEl}
            onClose={() => setProfileMenuAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                p: 2,
                width: 360,
                backgroundColor: isDarkMode ? "#1f1f1f" : "#fff",
                border: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="expenses-profile-select-label">
                  Select Profile
                </InputLabel>
                <Select
                  labelId="expenses-profile-select-label"
                  label="Select Profile"
                  value={expenseProfileId}
                  onChange={(e) => {
                    const next = String(e.target.value);
                    setExpenseProfileId(next);
                    updateUrlWithProfile(next);
                  }}
                >
                  {expenseProfiles.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Tooltip title="Save New">
                  <IconButton
                    size="small"
                    aria-label="Save New"
                    onClick={() => setSaveProfileDialogOpen(true)}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Update">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Update"
                      disabled={!expenseProfileId}
                      onClick={async () => {
                        const existing = expenseProfiles.find(
                          (p) => p.id === expenseProfileId
                        );
                        if (!existing) return;
                        await expensesProfilesDB.upsert({
                          ...existing,
                          settings: buildCurrentProfileSettings(),
                        });
                      }}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <SyncIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Delete">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Delete"
                      color="error"
                      disabled={!expenseProfileId}
                      onClick={async () => {
                        const existing = expenseProfiles.find(
                          (p) => p.id === expenseProfileId
                        );
                        if (!existing) return;
                        if (
                          !window.confirm(`Delete profile "${existing.name}"?`)
                        )
                          return;
                        await expensesProfilesDB.delete(expenseProfileId);
                        setExpenseProfileId("");
                        updateUrlWithProfile("");
                      }}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Popover>

          {!isLoading && expenses.length === 0 ? (
            <EmptyState
              icon={<ReceiptLongIcon />}
              title="No Expenses Yet"
              description="Start tracking your expenses to get insights into your spending patterns and manage your budget effectively."
              buttonText="Add Your First Expense"
              onAction={onOpen}
            />
          ) : (
            <ExpenseList
              expenses={pinnedAdjustedExpenses}
              projectionSources={visibleExpenses}
              searchQuery={appliedSearchQuery}
              sectionTotals={sectionTotals}
              onEditExpense={handleEditExpense}
              onDeleteExpense={handleDeleteExpense}
              onStopRecurringAfterDate={handleStopRecurringAfterDate}
              onMergeExpenseIntoSeries={handleMergeExpenseIntoSeries}
              onDuplicateExpense={handleDuplicateExpense}
              onShareExpense={handleShareExpense}
              onSkipExpense={handleSkipExpense}
              onPostponeExpense={handlePostponeExpense}
              onPostponeDebt={handlePostponeDebt}
              onRescheduleExpense={handleRescheduleExpense}
              onResetOccurrences={handleResetOccurrences}
              onMarkAsPaid={handleMarkAsPaid}
              onExpenseSync={handleExpenseSync}
              debts={debts}
              income={income}
              taxProfiles={taxProfiles}
              viewMode={viewMode}
              allExpanded={allExpanded}
              expandedSection={expandedSection}
              onSectionChange={handleSectionChange}
              groupingMode={groupingMode}
              highlightedExpenseId={newlyAddedExpenseId}
              pinnedExpenseIds={pinnedExpenseIds}
              onTogglePinExpense={togglePinExpense}
              onPausedIdsChange={handlePausedIdsChange}
              isLoading={isLoading}
              onLinkAsRecurring={handleBulkLinkRecurring}
              onMergeExpenses={handleOpenMergeDialog}
              onLinkTransaction={handleLinkTransaction}
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Expense Drawer */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        hideBackdrop={recurringBillsOpen || debtDrawerOpen}
        disableEnforceFocus={recurringBillsOpen || debtDrawerOpen}
        disableAutoFocus={recurringBillsOpen || debtDrawerOpen}
        ModalProps={
          recurringBillsOpen || debtDrawerOpen
            ? { sx: { pointerEvents: "none" } }
            : undefined
        }
        PaperProps={{
          sx: {
            width: 400,
            padding: 2,
            pointerEvents: "auto",
            // When the linked-debt editor is open, shift this panel left so both
            // sit side by side.
            right: debtDrawerOpen ? 400 : undefined,
            transition: "right 0.2s ease",
            backgroundColor: isDarkMode ? "#1a1a1a" : undefined,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">
            {isEditing ? "Edit Expense" : "Add Expense"}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <ExpenseForm
          onAddExpense={handleAddExpense}
          onUpdateExpense={handleUpdateExpense}
          onPaidStatusChange={(id, { isPaid, paymentsTotal }) => {
            console.log("[ExpensesPage] Payment recompute", {
              id,
              isPaid,
              paymentsTotal,
            });
            setExpenses((prev) => {
              let changed = false;
              const next = prev.map((e) => {
                if (e.id !== id) return e;
                if (e.isPaid === isPaid && e.paymentsTotal === paymentsTotal) {
                  return e;
                }
                changed = true;
                return { ...e, isPaid, paymentsTotal };
              });
              return changed ? next : prev;
            });
          }}
          expenseToEdit={currentExpense}
          isEditing={isEditing}
          debts={linkableDebts}
          income={income}
          taxProfiles={taxProfiles}
          expenseProfiles={expenseProfiles.map((p) => ({
            id: p.id,
            name: p.name,
          }))}
          defaultExpenseProfileId={expenseProfileId}
          onEditLinkedDebt={(debtId) => {
            const target = debts.find((d) => d.id === debtId);
            if (target) {
              setCurrentDebt(target);
              setDebtDrawerOpen(true);
            }
          }}
        />
      </Drawer>

      {/* Edit Debt Drawer (from the Recurring Bills view or the expense editor) */}
      <Drawer
        anchor="right"
        open={debtDrawerOpen}
        onClose={() => {
          setDebtDrawerOpen(false);
          setCurrentDebt(undefined);
        }}
        hideBackdrop={recurringBillsOpen || isOpen}
        disableEnforceFocus={recurringBillsOpen || isOpen}
        disableAutoFocus={recurringBillsOpen || isOpen}
        ModalProps={
          recurringBillsOpen || isOpen
            ? { sx: { pointerEvents: "none" } }
            : undefined
        }
        PaperProps={{
          sx: {
            width: 400,
            padding: 2,
            pointerEvents: "auto",
            backgroundColor: isDarkMode ? "#1a1a1a" : undefined,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Edit Debt</Typography>
          <IconButton
            onClick={() => {
              setDebtDrawerOpen(false);
              setCurrentDebt(undefined);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DebtForm
          isEditing
          debtToEdit={currentDebt}
          onAddDebt={() => {}}
          onUpdateDebt={handleUpdateDebt}
          expenses={expenses}
          isDarkMode={isDarkMode}
          onCancel={() => {
            setDebtDrawerOpen(false);
            setCurrentDebt(undefined);
          }}
        />
      </Drawer>

      <Dialog
        open={recurringEditDialogOpen}
        onClose={() => {
          const resolver = recurringEditResolverRef.current;
          recurringEditResolverRef.current = null;
          setRecurringEditDialogOpen(false);
          setRecurringEditTarget(null);
          resolver?.(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Recurring Expense</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {recurringEditTarget
              ? `"${recurringEditTarget.name}" is part of a recurring series. Apply this amount change to just this payment or all recurring payments going forward?`
              : "This expense is part of a recurring series. Apply this amount change to just this payment or all recurring payments going forward?"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={() => {
              const resolver = recurringEditResolverRef.current;
              recurringEditResolverRef.current = null;
              setRecurringEditDialogOpen(false);
              setRecurringEditTarget(null);
              resolver?.("just_this");
            }}
          >
            Just this one
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const resolver = recurringEditResolverRef.current;
              recurringEditResolverRef.current = null;
              setRecurringEditDialogOpen(false);
              setRecurringEditTarget(null);
              resolver?.("this_and_future");
            }}
          >
            All recurring payments
          </Button>
          <Button
            variant="text"
            onClick={() => {
              const resolver = recurringEditResolverRef.current;
              recurringEditResolverRef.current = null;
              setRecurringEditDialogOpen(false);
              setRecurringEditTarget(null);
              resolver?.(null);
            }}
            sx={{ width: "100%" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={recurringDeleteDialogOpen}
        onClose={() => {
          const resolver = recurringDeleteResolverRef.current;
          recurringDeleteResolverRef.current = null;
          setRecurringDeleteDialogOpen(false);
          setRecurringDeleteTarget(null);
          resolver?.(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Recurring Expense</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {recurringDeleteTarget
              ? `"${recurringDeleteTarget.name}" is part of a recurring series. What should be deleted?`
              : "This expense is part of a recurring series. What should be deleted?"}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1,
            px: 3,
            pb: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              const resolver = recurringDeleteResolverRef.current;
              recurringDeleteResolverRef.current = null;
              setRecurringDeleteDialogOpen(false);
              setRecurringDeleteTarget(null);
              resolver?.("just_this");
            }}
          >
            Delete just this instance
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              const resolver = recurringDeleteResolverRef.current;
              recurringDeleteResolverRef.current = null;
              setRecurringDeleteDialogOpen(false);
              setRecurringDeleteTarget(null);
              resolver?.("this_and_future");
            }}
          >
            Delete this and all future instances
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => {
              const resolver = recurringDeleteResolverRef.current;
              recurringDeleteResolverRef.current = null;
              setRecurringDeleteDialogOpen(false);
              setRecurringDeleteTarget(null);
              resolver?.("this_and_past");
            }}
          >
            Delete this and all past instances
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const resolver = recurringDeleteResolverRef.current;
              recurringDeleteResolverRef.current = null;
              setRecurringDeleteDialogOpen(false);
              setRecurringDeleteTarget(null);
              resolver?.("all_past_present_future");
            }}
          >
            Delete this, all future and past instances
          </Button>
          <Button
            variant="text"
            onClick={() => {
              const resolver = recurringDeleteResolverRef.current;
              recurringDeleteResolverRef.current = null;
              setRecurringDeleteDialogOpen(false);
              setRecurringDeleteTarget(null);
              resolver?.(null);
            }}
            sx={{ gridColumn: { xs: "1 / -1", sm: "1 / -1" } }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <LinkRecurringDialog
        open={linkRecurringDialogOpen}
        availableRoots={availableSeriesRoots}
        onConfirm={handleLinkAsRecurring}
        onCancel={() => {
          setLinkRecurringDialogOpen(false);
          setLinkRecurringTargetIds([]);
        }}
      />

      <MergeExpensesDialog
        open={mergeDialogOpen}
        candidates={mergeCandidates}
        onConfirm={handleMergeExpenses}
        onCancel={() => {
          setMergeDialogOpen(false);
          setMergeTargetIds([]);
        }}
      />

      {/* Edit Income Drawer */}
      {incomeDrawerOpen && (
        <Drawer
          anchor="right"
          open={incomeDrawerOpen}
          onClose={closeIncomeDrawer}
          PaperProps={{
            sx: {
              width: 400,
              padding: 2,
              backgroundColor: isDarkMode ? "#1a1a1a" : undefined,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {isIncomeEditing ? "Edit Income" : "Add Income"}
            </Typography>
            <IconButton onClick={closeIncomeDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>
          <IncomeForm
            onAddIncome={handleAddIncome}
            onUpdateIncome={handleUpdateIncome}
            incomeToEdit={currentIncome || undefined}
            isEditing={isIncomeEditing}
          />
        </Drawer>
      )}

      {/* Floating Action Buttons */}
      <FloatingActionMenu label="Expense actions">
        {/* Import CSV FAB */}
        <Tooltip title="Import Expenses CSV" placement="left" arrow>
          <Fab
            color="info"
            size="large"
            onClick={handleImportClick}
            disabled={isImporting}
            sx={{
              opacity: isImporting ? 0.5 : 1,
            }}
          >
            {isImporting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <FileUploadIcon />
            )}
          </Fab>
        </Tooltip>

        {/* Auto-Link Transactions FAB */}
        {!demoService.isDemo() && (
          <Tooltip title="Auto-Link Transactions" placement="left" arrow>
            <Fab
              color="secondary"
              size="large"
              onClick={runTransactionAnalysis}
              disabled={isAnalyzing}
              sx={{
                opacity: isAnalyzing ? 0.5 : 1,
              }}
            >
              {isAnalyzing ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <SyncIcon />
              )}
            </Fab>
          </Tooltip>
        )}

        {/* Add Expense FAB */}
        <Tooltip title="Add Expense" placement="left" arrow>
          <Fab color="primary" size="large" aria-label="add" onClick={onOpen}>
            <AddIcon />
          </Fab>
        </Tooltip>
      </FloatingActionMenu>

      {/* Analysis Status */}
      <Snackbar
        open={!!lastAnalysis}
        autoHideDuration={6000}
        onClose={() => setLastAnalysis(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={() => setLastAnalysis(null)} severity="info">
          {lastAnalysis}
        </Alert>
      </Snackbar>

      {/* Undo Notification */}
      <Snackbar
        open={undoSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setUndoSnackbar({ open: false, message: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={undoSnackbar.message}
        action={
          undoSnackbar.canUndo && undoStack.length > 0 ? (
            <Button
              color="secondary"
              size="small"
              onClick={() => {
                setUndoSnackbar({ open: false, message: "" });
                handleUndo();
              }}
            >
              UNDO
            </Button>
          ) : undefined
        }
      />
      <ShareRecordModal
        open={shareOpen}
        onClose={() => {
          setShareOpen(false);
          setShareExpense(null);
        }}
        groups={groups}
        onShare={handleShareRecord}
      />
      <PageShareModal
        open={pageShareOpen}
        onClose={() => {
          setPageShareOpen(false);
          const next = new URLSearchParams(searchParams);
          next.delete("share");
          setSearchParams(next, { replace: true });
        }}
        pageType="expenses"
        expenseItems={expenses}
      />

      <SaveExpensesProfileDialog
        open={saveProfileDialogOpen}
        onClose={() => setSaveProfileDialogOpen(false)}
        onSave={async (name) => {
          const profile: ExpensesProfile = {
            id: generateExpensesProfileId(),
            name,
            settings: buildCurrentProfileSettings(),
            createdAt: Date.now(),
          };
          const saved = await expensesProfilesDB.upsert(profile);
          if (saved) {
            setExpenseProfileId(saved.id);
            updateUrlWithProfile(saved.id);
          }
          setSaveProfileDialogOpen(false);
        }}
      />

      <Dialog
        open={overdueIncomeBreakdownOpen}
        onClose={() => setOverdueIncomeBreakdownOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Overdue Income Breakdown</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Button
              variant={
                includePendingDepositsInOverdue ? "contained" : "outlined"
              }
              size="small"
              onClick={() =>
                setIncludePendingDepositsInOverdue((prev) => !prev)
              }
            >
              {includePendingDepositsInOverdue
                ? "Pending Deposits: Included"
                : "Pending Deposits: Excluded"}
            </Button>
            <Button
              variant={
                includeAvailableBalancesInOverdue ? "contained" : "outlined"
              }
              size="small"
              onClick={() =>
                setIncludeAvailableBalancesInOverdue((prev) => !prev)
              }
            >
              {includeAvailableBalancesInOverdue
                ? "Available Balances: Included"
                : "Available Balances: Excluded"}
            </Button>
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Overdue Income
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {incomeUtils.formatCurrency(overdueIncomeBaseTotal)}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
            Pending Deposits ({pendingDepositIncomeItems.length})
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {incomeUtils.formatCurrency(pendingDepositIncomeTotal)}
          </Typography>
          {pendingDepositIncomeItems.slice(0, 12).map((item) => (
            <Typography
              key={`pending-${item.id}`}
              variant="caption"
              sx={{ display: "block", color: "text.secondary" }}
            >
              {item.client} •{" "}
              {item.depositDate
                ? new Date(item.depositDate).toLocaleDateString("en-US", {
                    timeZone: "UTC",
                  })
                : "-"}{" "}
              •{" "}
              {incomeUtils.formatCurrency(
                incomeUtils.getNetDepositedTotal(item)
              )}
            </Typography>
          ))}
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Available Account Balances ({availableBalanceAccounts.length})
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {incomeUtils.formatCurrency(availableBalanceTotal)}
          </Typography>
          {availableBalanceAccounts.slice(0, 12).map((account) => (
            <Typography
              key={`acct-${account.accountId}`}
              variant="caption"
              sx={{ display: "block", color: "text.secondary" }}
            >
              {account.name} ({account.type}) •{" "}
              {incomeUtils.formatCurrency(account.resolvedAvailable || 0)}
            </Typography>
          ))}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Included Overdue Income Total:{" "}
            {incomeUtils.formatCurrency(overdueIncomeAugmentedTotal)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverdueIncomeBreakdownOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Income Details Modal */}
      <Dialog
        open={incomeModalOpen}
        onClose={() => {
          setIncomeModalOpen(false);
          setIncomeModalSection(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1a1a1a" : "#fff",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
          }}
        >
          <Typography variant="h6" component="span">
            Income for{" "}
            {incomeModalSection
              ? incomeModalSection.charAt(0).toUpperCase() +
                incomeModalSection.slice(1).replace(/([A-Z])/g, " $1")
              : ""}
          </Typography>
          <IconButton
            onClick={() => {
              setIncomeModalOpen(false);
              setIncomeModalSection(null);
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {incomeModalSection && categorizedIncome[incomeModalSection] && (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expected</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Frequency</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categorizedIncome[incomeModalSection].length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No income items in this section
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    categorizedIncome[incomeModalSection].map((inc) => (
                      <TableRow
                        key={inc.id}
                        sx={{
                          "&:hover": {
                            backgroundColor: isDarkMode
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                          },
                        }}
                      >
                        <TableCell>{inc.client}</TableCell>
                        <TableCell>
                          {inc.invoiceLink ? (
                            <a
                              href={inc.invoiceLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#805AD5",
                                textDecoration: "none",
                              }}
                            >
                              {inc.invoiceNumber || "View"}
                            </a>
                          ) : (
                            inc.invoiceNumber || "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {editingIncomeId === inc.id ? (
                            <TextField
                              type="date"
                              size="small"
                              value={editingExpectedDate}
                              onChange={(e) =>
                                setEditingExpectedDate(e.target.value)
                              }
                              onBlur={() => saveEditExpectedDate(inc)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  saveEditExpectedDate(inc);
                                }
                                if (e.key === "Escape") {
                                  cancelEditExpectedDate();
                                }
                              }}
                              disabled={editingExpectedSaving}
                              sx={{ width: 150 }}
                            />
                          ) : (
                            <Typography
                              sx={{
                                cursor: "pointer",
                                "&:hover": {
                                  textDecoration: "underline",
                                },
                              }}
                              onClick={() => startEditExpectedDate(inc)}
                            >
                              {formatExpectedDate(inc.expectedByDate)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {inc.isRecurring
                            ? inc.recurrencePattern || "Recurring"
                            : "One-time"}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {expenseUtils.formatCurrency(inc.invoiceTotal || 0)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {/* Section Total */}
          {incomeModalSection && categorizedIncome[incomeModalSection] && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                p: 2,
                borderTop: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
                backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Total:{" "}
                {expenseUtils.formatCurrency(
                  categorizedIncome[incomeModalSection].reduce(
                    (sum, inc) => sum + (inc.invoiceTotal || 0),
                    0
                  )
                )}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{ borderTop: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}` }}
        >
          <Button
            onClick={() => {
              setIncomeModalOpen(false);
              setIncomeModalSection(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <LoadingTimerDebug
        loading={isLoading}
        label="Expenses"
        details={loadingDebugDetails}
      />
    </>
  );
};

export default ExpensesPage;
